/**
 * Página de Detalle de Evento - Vista Completa
 * Panel ampliado con tabs para gestión completa del evento
 */

import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Button,
  SectionBadge,
} from "@/components/ui";
import { Modal, ModalFooter } from "@/components/ui/Modal";
import {
  Calendar,
  MapPin,
  Clock,
  ArrowLeft,
  Edit,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mail,
  Phone,
  Users,
  DollarSign,
  ClipboardList,
  Settings,
  BarChart3,
  Send,
  Play,
  Square,
  Hourglass,
  Building2,
  TrendingUp,
  Trophy,
  FileText,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Download,
  UserCheck,
  Search,
  UserPlus,
  Upload,
  Lock,
  Pencil,
  Activity,
  FileCheck,
  ArrowRightLeft,
  ClipboardCheck,
} from "lucide-react";
import {
  useEvent,
  useUpdateEventStatus,
  useEventRegistrations,
  useClubEventRegistrations,
  useUpdateRegistrationStatus,
  useCancelEventRegistration,
  useUpdateEventRegistration,
  useEventPayments,
  useClubEventPayments,
  useClubEventRanking,
} from "@/hooks/useEvents";
import { usePermissions } from "@/hooks/usePermissions";
import { Can } from "@/components/auth/Can";
import { useAccessibleClubs } from "@/hooks/useDataScope";
import { useConfirm } from "@/components/ui/ConfirmDialog";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import {
  getEventStatusLabel,
  getEventStatusColor,
} from "@/lib/services/events";
import { formatCurrency } from "@/lib/utils";
import { exportToXlsx } from "@/lib/utils/exportXlsx";
import { buildPersonsMap, personColumns } from "@/lib/utils/eventExport";
import { usePersons } from "@/hooks/usePersons";
import EventModal from "./components/EventModal";
import EventRankingTab from "./components/EventRankingTab";
import { EventActivitiesTab } from "./components/EventActivitiesTab";
import PaymentValidationPanel from "./components/PaymentValidationPanel";
import EventAgendaPanel from "./components/EventAgendaPanel";
import EventRegistrationModal from "./components/EventRegistrationModal";
import PaymentUploadModal from "./components/PaymentUploadModal";
import EventConsentGate from "@/components/consent/EventConsentGate";
import ConsentStatusBadge from "@/components/consent/ConsentStatusBadge";
import DirectorConsentLauncher from "@/components/consent/DirectorConsentLauncher";
import MemberConsentDocsModal from "@/components/consent/MemberConsentDocsModal";
import { useEventConsents } from "@/hooks/useEventConsents";
import {
  useEventActivities,
  useEventActivityScores,
} from "@/hooks/useEventActivities";
import { useClubSubmissions } from "@/hooks/useActivitySubmissions";
import {
  canDirectorEdit,
  toJsDate as toSubJsDate,
} from "@/lib/services/activitySubmissions";
import EventClubsTab from "./components/EventClubsTab";
import EventClubAttachmentsPanel from "@/components/events/EventClubAttachmentsPanel";
import AdminRegistrationEditModal from "./components/AdminRegistrationEditModal";
import EditCategoryModal from "./components/EditCategoryModal";
import PlanillaInscripcionModal from "./components/PlanillaInscripcionModal";
import SwapRegistrationModal from "./components/SwapRegistrationModal";
import { useQuery } from "@tanstack/react-query";
import { churchService } from "@/lib/services/organization";
import { isClubDiscounted } from "@/lib/pricing";
import {
  isParticipantCategory,
  isPlanillaRegistration,
} from "@/lib/planilla/planillaInscripcion";
import type {
  EventStatus,
  EventType,
  ClubType,
  EventRegistration,
} from "@/types";

// Helper para convertir timestamp a Date de forma segura
const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return new Date();
};

const eventTypeLabels: Record<EventType, string> = {
  camporee: "🏕️ Camporee",
  campamento: "⛺ Campamento",
  convocatoria: "📢 Convocatoria",
  entrenamiento: "📚 Entrenamiento",
  investidura: "🎖️ Investidura",
  social: "🎉 Social",
  servicio: "🤝 Servicio Comunitario",
  otro: "📌 Otro",
};

const clubTypeLabels: Record<ClubType, string> = {
  aventureros: "Aventureros",
  conquistadores: "Conquistadores",
  guias_mayores: "Guías Mayores",
};

type TabType =
  | "overview"
  | "registrations"
  | "ranking"
  | "payments"
  | "settings"
  | "members"
  | "clubs"
  | "activities"
  | "submissions"
  | "documents"
  | "agenda";

const VALID_TABS: ReadonlyArray<TabType> = [
  "overview",
  "registrations",
  "ranking",
  "payments",
  "settings",
  "members",
  "clubs",
  "activities",
  "submissions",
  "documents",
  "agenda",
];

function readTabFromHash(): TabType | null {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash.replace(/^#/, "").trim();
  const [tab] = raw.split("/");
  return (VALID_TABS as readonly string[]).includes(tab)
    ? (tab as TabType)
    : null;
}

/**
 * Soporta deep-link a una entrega específica:
 *   /events/<eventId>#submissions/<activityId>
 */
function readActivityFromHash(): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash.replace(/^#/, "").trim();
  const [tab, rest] = raw.split("/");
  if (tab === "submissions" && rest) return rest;
  return null;
}

/** Hook que retorna `Date.now()` y se actualiza cada `intervalMs` para refrescar countdowns. */
function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>(
    () => readTabFromHash() ?? "overview",
  );
  const [hashActivityId, setHashActivityId] = useState<string | null>(() =>
    readActivityFromHash(),
  );

  // Sincroniza el hash de la URL con la pestaña activa (deep links).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const current = window.location.hash.replace(/^#/, "");
    const currentTab = current.split("/")[0];
    if (currentTab !== activeTab) {
      const newUrl = `${window.location.pathname}${window.location.search}#${activeTab}`;
      window.history.replaceState(null, "", newUrl);
      setHashActivityId(null);
    }
  }, [activeTab]);

  // Si el usuario cambia el hash (back/forward o link externo), actualiza tab.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      const t = readTabFromHash();
      if (t) setActiveTab(t);
      setHashActivityId(readActivityFromHash());
    };
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);
  const [showEditModal, setShowEditModal] = useState(false);
  const [expandedClubs, setExpandedClubs] = useState<Set<string>>(new Set());
  const [clubSearchTerm, setClubSearchTerm] = useState("");

  const [showPaymentsPanel, setShowPaymentsPanel] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  // Admin: edición avanzada de inscripción
  const [adminEditReg, setAdminEditReg] = useState<EventRegistration | null>(
    null,
  );

  // Director / secretario: cambiar tipo de entrada (categoría) sin cancelar
  const [editCategoryReg, setEditCategoryReg] =
    useState<EventRegistration | null>(null);
  const [swapReg, setSwapReg] = useState<EventRegistration | null>(null);

  // Director: planilla oficial de inscripción (Excel / PDF)
  const [showPlanillaModal, setShowPlanillaModal] = useState(false);

  // Director: filtros y orden de la tabla "Mis Inscritos"
  const [dirSearch, setDirSearch] = useState("");
  const [dirStatusFilter, setDirStatusFilter] = useState<
    "all" | "pending" | "approved" | "waitlist"
  >("all");
  const [dirCategoryFilter, setDirCategoryFilter] = useState<string>("all");
  const [dirSortKey, setDirSortKey] = useState<
    "name" | "category" | "status" | "price"
  >("name");
  const [dirSortDir, setDirSortDir] = useState<"asc" | "desc">("asc");

  // Director modal states
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showPaymentUploadModal, setShowPaymentUploadModal] = useState(false);
  // Admin/organizador: inscribir miembros de un club específico (incluso con
  // inscripciones cerradas por fecha — control total del admin).
  const [adminRegisterClub, setAdminRegisterClub] = useState<{
    id: string;
    name: string;
    churchId?: string;
  } | null>(null);
  // Confirmación inline para cancelar inscripción
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  // Rechazo de inscripción con motivo
  const [rejectRegistrationId, setRejectRegistrationId] = useState<
    string | null
  >(null);
  const [rejectReasonDraft, setRejectReasonDraft] = useState("");

  // Visor de documentos del consentimiento (director / asociación)
  const [docsModal, setDocsModal] = useState<{
    consent: import("@/types").EventConsent | null;
    memberName: string;
  } | null>(null);

  // Tick global cada segundo para countdowns en vivo (debe ir antes de cualquier early return).
  const now = useNow(1000);

  const { data: event, isLoading } = useEvent(id || "");
  const { role } = usePermissions();
  const { data: clubs } = useAccessibleClubs();

  // Permisos (se calculan temprano para poder SCOPEAR las lecturas y ahorrar).
  const myClubId = clubs?.[0]?.id;
  const myClubName = clubs?.[0]?.name;
  const myClubChurchId = clubs?.[0]?.churchId;
  const isDirectorView = role === "club_director";
  const isAdmin = role === "admin_association" || role === "admin_union";
  const isOrganizer =
    event?.organizerId === myClubId ||
    (role === "admin_association" && event?.level === "association") ||
    (role === "admin_union" && event?.level === "union");

  // AHORRO DE LECTURAS (2026-06-24): un director que NO organiza carga SOLO las
  // inscripciones de su club; admin/organizador cargan todas las del evento.
  // Antes TODOS suscribían a las ~1.384 inscripciones del evento en cada
  // apertura → multiplicado por decenas de directores = el pico de lecturas.
  const directorScoped = isDirectorView && !isOrganizer;
  const { data: allEventRegistrations } = useEventRegistrations(
    directorScoped ? undefined : id,
  );
  const { data: clubScopedRegistrations } = useClubEventRegistrations(
    directorScoped ? myClubId : undefined,
    directorScoped ? id : undefined,
  );
  const registrations = directorScoped
    ? clubScopedRegistrations
    : allEventRegistrations;

  const { data: eventPayments } = useEventPayments(id);
  const { data: persons } = usePersons();
  const updateStatusMutation = useUpdateEventStatus();
  const updateRegistrationStatus = useUpdateRegistrationStatus();
  const cancelRegistration = useCancelEventRegistration();
  const updateRegistration = useUpdateEventRegistration();
  const confirm = useConfirm();

  // Director-specific data: club's own payments and ranking
  const { data: myClubPayments } = useClubEventPayments(
    isDirectorView ? myClubId : undefined,
    isDirectorView ? id : undefined,
  );
  const { data: myClubRanking } = useClubEventRanking(
    isDirectorView ? myClubId : undefined,
    isDirectorView ? id : undefined,
  );

  // Resolver el distrito del club para saber si aplica descuento
  const eventHasDistrictDiscount =
    (event?.discountedDistrictIds?.length ?? 0) > 0;
  const { data: myClubChurch } = useQuery({
    queryKey: ["church", myClubChurchId],
    queryFn: () =>
      myClubChurchId
        ? churchService.getById(myClubChurchId)
        : Promise.resolve(null),
    enabled: !!myClubChurchId && eventHasDistrictDiscount,
    staleTime: 5 * 60 * 1000,
  });
  const myClubIsDiscounted = useMemo(
    () =>
      event
        ? isClubDiscounted(
            myClubChurch?.districtId ?? myClubChurch?.alfoliDistrictId,
            event,
          )
        : false,
    [myClubChurch?.districtId, event],
  );

  // Estado de consentimientos del evento (en tiempo real)
  const { byMemberId: consentByMember, progressFor: consentProgressFor } =
    useEventConsents(id, event?.consentChecklistConfig);

  // Actividades + entregas del club (para alerta de pendientes en vista director)
  const { data: eventActivities } = useEventActivities(
    isDirectorView ? id : undefined,
  );
  const { data: myClubSubmissionsData } = useClubSubmissions(
    isDirectorView ? id : undefined,
    isDirectorView ? myClubId : undefined,
  );
  const pendingSubmissions = useMemo(() => {
    if (!isDirectorView || !eventActivities) return [];
    const subs = myClubSubmissionsData ?? [];
    const subByActivity = new Map(subs.map((s) => [s.activityId, s]));
    return eventActivities.filter((act) => {
      const req = act.submissionRequirements;
      if (!req?.enabled) return false;
      const sub = subByActivity.get(act.id);
      // Falta entregar o fue rechazada → pendiente.
      if (!sub || sub.status === "draft" || sub.reviewStatus === "rejected") {
        const perm = canDirectorEdit(act, sub);
        return perm.allowed;
      }
      return false;
    });
  }, [isDirectorView, eventActivities, myClubSubmissionsData]);

  // Activity ranking position (for director stats card)
  const { data: activityScores } = useEventActivityScores(
    isDirectorView ? id : undefined,
  );
  const activityRankPosition = useMemo(() => {
    if (!activityScores || !myClubId) return null;
    // Compute total per club using dense ranking
    const totals = new Map<string, number>();
    activityScores.forEach((s) => {
      totals.set(s.clubId, (totals.get(s.clubId) ?? 0) + s.points);
    });
    const myTotal = totals.get(myClubId) ?? 0;
    if (myTotal === 0) return null;
    // Dense rank: count distinct totals greater than mine
    const higherTotals = new Set<number>();
    totals.forEach((t) => {
      if (t > myTotal) higherTotals.add(t);
    });
    return higherTotals.size + 1;
  }, [activityScores, myClubId]);

  const activityTotalPoints = useMemo(() => {
    if (!activityScores || !myClubId) return 0;
    return activityScores
      .filter((s) => s.clubId === myClubId)
      .reduce((sum, s) => sum + s.points, 0);
  }, [activityScores, myClubId]);

  // My club's registrations (filtered from all registrations)
  const myClubRegistrations = useMemo(() => {
    if (!registrations || !myClubId) return [];
    return registrations.filter((r) => r.clubId === myClubId);
  }, [registrations, myClubId]);

  const myActiveRegistrations = useMemo(
    () =>
      myClubRegistrations.filter((r) =>
        ["pending", "approved", "waitlist"].includes(r.status),
      ),
    [myClubRegistrations],
  );

  // ── PLANILLA OFICIAL: marca de uniforme ──
  //
  // La planilla de la Asociación separa a los aventureros uniformados de los
  // "amigos sin uniforme". No es un dato de la ficha (el mismo niño puede ir
  // uniformado un año y de visita el siguiente), así que vive en la
  // inscripción y lo marca el director acá.
  const toggleUniform = (reg: EventRegistration, value: boolean) => {
    updateRegistration.mutate({
      id: reg.id,
      eventId: reg.eventId,
      data: { hasUniform: value },
    });
  };

  /** Marca con uniforme a todos los aventureros vivos que estén sin marcar. */
  const markAllUniform = async () => {
    const pendientes = myClubRegistrations.filter(
      (r) =>
        isPlanillaRegistration(r) &&
        isParticipantCategory(r.category) &&
        r.hasUniform !== true,
    );
    if (pendientes.length === 0) return;
    // En serie y no en paralelo: son 20-30 escrituras y un Promise.all de
    // golpe hace que la suscripción repinte la tabla en cada una.
    for (const reg of pendientes) {
      await updateRegistration.mutateAsync({
        id: reg.id,
        eventId: reg.eventId,
        data: { hasUniform: true },
      });
    }
    toast.success(
      `${pendientes.length} ${pendientes.length === 1 ? "aventurero marcado" : "aventureros marcados"} con uniforme`,
    );
  };

  // Categorías presentes en mis inscripciones (para el filtro)
  const dirAvailableCategories = useMemo(() => {
    const set = new Set<string>();
    myActiveRegistrations.forEach((r) => {
      if (r.category) set.add(r.category);
    });
    return Array.from(set);
  }, [myActiveRegistrations]);

  // Inscripciones filtradas + ordenadas para mostrar en la tabla del director
  const displayedRegistrations = useMemo(() => {
    const norm = (s: string) =>
      s
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    const q = norm(dirSearch);
    let list = myActiveRegistrations.filter((r) => {
      if (dirStatusFilter !== "all" && r.status !== dirStatusFilter)
        return false;
      if (dirCategoryFilter !== "all" && r.category !== dirCategoryFilter)
        return false;
      if (q && !norm(r.personName || "").includes(q)) return false;
      return true;
    });
    const statusOrder: Record<string, number> = {
      approved: 0,
      pending: 1,
      waitlist: 2,
    };
    list = [...list].sort((a, b) => {
      let cmp = 0;
      switch (dirSortKey) {
        case "name":
          cmp = (a.personName || "").localeCompare(b.personName || "", "es");
          break;
        case "category":
          cmp = (a.category || "").localeCompare(b.category || "", "es");
          break;
        case "status":
          cmp = (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
          break;
        case "price":
          cmp = (a.unitPrice || 0) - (b.unitPrice || 0);
          break;
      }
      return dirSortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [
    myActiveRegistrations,
    dirSearch,
    dirStatusFilter,
    dirCategoryFilter,
    dirSortKey,
    dirSortDir,
  ]);

  const toggleDirSort = (key: "name" | "category" | "status" | "price") => {
    if (dirSortKey === key) {
      setDirSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setDirSortKey(key);
      setDirSortDir("asc");
    }
  };

  // IDs de personas ya inscritas (para evitar duplicados en modal)
  const registeredPersonIds = useMemo(
    () =>
      myActiveRegistrations
        .map((r) => r.personId || r.memberId)
        .filter((id): id is string => Boolean(id)),
    [myActiveRegistrations],
  );

  // Estadísticas de inscripciones
  const stats = useMemo(() => {
    if (!registrations)
      return {
        total: 0,
        approved: 0,
        pending: 0,
        rejected: 0,
        cancelled: 0,
        waitlist: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0,
        byCategory: {} as Record<string, { count: number; amount: number }>,
        byClub: [] as {
          clubId: string;
          clubName: string;
          count: number;
          approved: number;
          pending: number;
          paid: number;
          amount: number;
        }[],
        byPaymentStatus: { pending: 0, partial: 0, paid: 0, refunded: 0 },
      };

    const byCategory: Record<string, { count: number; amount: number }> = {};
    const byClubMap: Record<
      string,
      {
        clubId: string;
        clubName: string;
        count: number;
        approved: number;
        pending: number;
        paid: number;
        amount: number;
      }
    > = {};
    const byPaymentStatus = { pending: 0, partial: 0, paid: 0, refunded: 0 };

    let approved = 0,
      pending = 0,
      rejected = 0,
      cancelled = 0,
      waitlist = 0;
    let totalAmount = 0;

    registrations.forEach((r) => {
      // Por estado
      if (r.status === "approved") approved++;
      else if (r.status === "pending") pending++;
      else if (r.status === "rejected") rejected++;
      else if (r.status === "cancelled") cancelled++;
      else if (r.status === "waitlist") waitlist++;

      // Solo contar aprobados para estadísticas financieras
      const countForFinancials = r.status === "approved";

      // Por categoría (solo aprobados)
      if (countForFinancials) {
        if (!byCategory[r.category]) {
          byCategory[r.category] = { count: 0, amount: 0 };
        }
        byCategory[r.category].count++;
        byCategory[r.category].amount += r.unitPrice;
      }

      // Por club (solo activos: pending, approved, waitlist)
      const isActive = !["rejected", "cancelled"].includes(r.status);
      if (isActive) {
        if (!byClubMap[r.clubId]) {
          byClubMap[r.clubId] = {
            clubId: r.clubId,
            clubName: r.clubName,
            count: 0,
            approved: 0,
            pending: 0,
            paid: 0,
            amount: 0,
          };
        }
        byClubMap[r.clubId].count++;
        if (r.status === "approved") byClubMap[r.clubId].approved++;
        if (r.status === "pending") byClubMap[r.clubId].pending++;
        if (r.paymentStatus === "paid" && r.status === "approved")
          byClubMap[r.clubId].paid++;
        // Solo sumar monto de aprobados
        if (countForFinancials) {
          byClubMap[r.clubId].amount += r.unitPrice;
        }
      }

      // Por estado de pago (solo aprobados)
      if (countForFinancials && r.paymentStatus) {
        byPaymentStatus[r.paymentStatus]++;
      }

      // Montos (solo aprobados)
      if (countForFinancials) {
        totalAmount += r.unitPrice;
      }
    });

    // paidAmount se calcula desde los pagos aprobados, NO desde r.amountPaid
    const paidAmount = (eventPayments ?? [])
      .filter((p) => p.status === "approved")
      .reduce((sum, p) => sum + p.amount, 0);

    return {
      // Total solo cuenta activos (excluye cancelled y rejected)
      total: approved + pending + waitlist,
      approved,
      pending,
      rejected,
      cancelled,
      waitlist,
      totalAmount,
      paidAmount,
      pendingAmount: totalAmount - paidAmount,
      byCategory,
      byClub: Object.values(byClubMap).sort((a, b) => b.count - a.count),
      byPaymentStatus,
    };
  }, [registrations, eventPayments]);

  // Inscripciones agrupadas por club
  const registrationsByClub = useMemo(() => {
    if (!registrations) return new Map<string, EventRegistration[]>();
    const map = new Map<string, EventRegistration[]>();
    registrations.forEach((r) => {
      if (!map.has(r.clubId)) {
        map.set(r.clubId, []);
      }
      map.get(r.clubId)!.push(r);
    });
    return map;
  }, [registrations]);

  // Filtrar clubes por búsqueda
  const filteredClubs = useMemo(() => {
    if (!clubSearchTerm) return stats.byClub;
    const term = clubSearchTerm.toLowerCase();
    return stats.byClub.filter((club) =>
      club.clubName.toLowerCase().includes(term),
    );
  }, [stats.byClub, clubSearchTerm]);

  // Exportar inscripciones consolidadas (todos los clubes visibles) a Excel
  const handleExportConsolidated = () => {
    if (!registrations || registrations.length === 0) return;
    const REG_STATUS_LABEL: Record<string, string> = {
      pending: "Pendiente",
      approved: "Aprobado",
      rejected: "Rechazado",
      cancelled: "Cancelado",
      waitlist: "Lista de espera",
    };
    const CATEGORY_LABEL: Record<string, string> = {
      pathfinder: "Conquistador",
      staff: "Staff",
      guest: "Invitado",
      family: "Familiar",
    };
    const PAY_STATUS_LABEL: Record<string, string> = {
      pending: "Pendiente",
      partial: "Parcial",
      paid: "Pagado",
      refunded: "Reembolsado",
    };
    const visibleClubIds = new Set(filteredClubs.map((c) => c.clubId));
    const rows = registrations.filter(
      (r) =>
        visibleClubIds.has(r.clubId) &&
        r.status !== "cancelled" &&
        r.status !== "rejected",
    );
    const safeName = (event?.name || "evento")
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "_");
    const stamp = new Date().toISOString().slice(0, 10);
    const personsMap = buildPersonsMap(persons);
    exportToXlsx({
      filename: `inscripciones_${safeName}_${stamp}`,
      sheetName: "Inscripciones",
      rows,
      columns: [
        { header: "Club", key: (r) => r.clubName, width: 28 },
        { header: "Nombre", key: (r) => r.personName, width: 32 },
        ...personColumns(personsMap),
        {
          header: "Categoría",
          key: (r) => CATEGORY_LABEL[r.category] || r.category,
          width: 14,
        },
        {
          header: "Estado",
          key: (r) => REG_STATUS_LABEL[r.status] || r.status,
          width: 14,
        },
        {
          header: "Estado Pago",
          key: (r) =>
            PAY_STATUS_LABEL[r.paymentStatus as string] || r.paymentStatus,
          width: 14,
        },
        { header: "Monto", key: (r) => r.unitPrice ?? 0, width: 14 },
        {
          header: "Fecha Inscripción",
          key: (r) => {
            const d = r.registrationDate as unknown as
              | { toDate?: () => Date }
              | Date
              | undefined;
            if (!d) return "";
            const date =
              d instanceof Date
                ? d
                : typeof d.toDate === "function"
                  ? d.toDate()
                  : null;
            return date ? date.toLocaleDateString("es-CO") : "";
          },
          width: 16,
        },
      ],
    });
  };

  const handleStatusChange = async (newStatus: EventStatus) => {
    if (!event) return;

    const confirmMessages: Record<EventStatus, string> = {
      draft: "¿Volver a borrador? Las inscripciones se cerrarán.",
      published: "¿Publicar este evento? Los clubes podrán inscribirse.",
      registration_closed:
        "¿Cerrar inscripciones? No se aceptarán más inscripciones.",
      in_progress: "¿Marcar como en progreso? El evento ha comenzado.",
      completed: "¿Marcar como completado? El evento ha terminado.",
      cancelled: "¿Cancelar este evento? Esta acción no se puede deshacer.",
    };

    const ok = await confirm({
      title: "Cambiar estado del evento",
      description: confirmMessages[newStatus],
      variant: newStatus === "cancelled" ? "danger" : "warning",
    });
    if (ok) {
      await updateStatusMutation.mutateAsync({
        id: event.id,
        status: newStatus,
      });
    }
  };

  const handleApproveRegistration = async (registrationId: string) => {
    if (!event) return;
    await updateRegistrationStatus.mutateAsync({
      id: registrationId,
      status: "approved",
      eventId: event.id,
    });
  };

  const handleRejectRegistration = (registrationId: string) => {
    setRejectRegistrationId(registrationId);
    setRejectReasonDraft("");
  };

  const submitRejectRegistration = async () => {
    if (!event || !rejectRegistrationId) return;
    await updateRegistrationStatus.mutateAsync({
      id: rejectRegistrationId,
      status: "rejected",
      eventId: event.id,
      rejectionReason: rejectReasonDraft.trim() || undefined,
    });
    setRejectRegistrationId(null);
    setRejectReasonDraft("");
  };

  // Aprobar todas las inscripciones pendientes de un club
  const handleApproveClub = async (clubId: string) => {
    if (!event || !registrations) return;
    const pending = registrations.filter(
      (r) => r.clubId === clubId && r.status === "pending",
    );
    if (pending.length === 0) return;

    setIsBulkProcessing(true);
    let ok = 0,
      fail = 0;
    for (const r of pending) {
      try {
        await updateRegistrationStatus.mutateAsync({
          id: r.id,
          status: "approved",
          eventId: event.id,
        });
        ok++;
      } catch {
        fail++;
      }
    }
    setIsBulkProcessing(false);
    if (fail === 0) toast.success(`${ok} inscripción(es) aprobada(s)`);
    else toast.error(`${ok} ok, ${fail} con error`);
  };

  // Aprobar TODOS los pendientes del evento
  const handleApproveAllPending = async () => {
    if (!event || !registrations) return;
    const pending = registrations.filter((r) => r.status === "pending");
    if (pending.length === 0) return;
    const confirmed = await confirm({
      title: "Aprobar pendientes",
      description: `¿Aprobar ${pending.length} inscripción(es) pendiente(s)?`,
      variant: "info",
      confirmText: "Aprobar todas",
    });
    if (!confirmed) return;

    setIsBulkProcessing(true);
    let ok = 0,
      fail = 0;
    for (const r of pending) {
      try {
        await updateRegistrationStatus.mutateAsync({
          id: r.id,
          status: "approved",
          eventId: event.id,
        });
        ok++;
      } catch {
        fail++;
      }
    }
    setIsBulkProcessing(false);
    if (fail === 0) toast.success(`¡${ok} inscripción(es) aprobada(s)!`);
    else toast.error(`${ok} ok, ${fail} con error`);
  };

  const formatDate = (timestamp: unknown) => {
    return toSafeDate(timestamp).toLocaleDateString("es-CO", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatShortDate = (timestamp: unknown) => {
    return toSafeDate(timestamp).toLocaleDateString("es-CO", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTime = (timestamp: unknown) => {
    return toSafeDate(timestamp).toLocaleTimeString("es-CO", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toggleClubExpand = (clubId: string) => {
    const newSet = new Set(expandedClubs);
    if (newSet.has(clubId)) {
      newSet.delete(clubId);
    } else {
      newSet.add(clubId);
    }
    setExpandedClubs(newSet);
  };

  const getCategoryLabel = (category: string) => {
    // Buscar en las categorías definidas en el evento
    const pricing = event?.pricing.find((p) => p.category === category);
    if (pricing?.label) {
      return pricing.label;
    }
    // Fallback para categorías estándar
    const defaultLabels: Record<string, string> = {
      pathfinder: "Conquistador/Aventurero",
      staff: "Staff/Consejero",
      guest: "Invitado",
      family: "Familiar",
    };
    return defaultLabels[category] || category;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
          <AlertCircle className="w-8 h-8 text-gray-500" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900">
          Evento no encontrado
        </h2>
        <Link
          to="/events"
          className="text-purple-600 hover:underline mt-2 inline-block"
        >
          Volver a eventos
        </Link>
      </div>
    );
  }

  // ── Cálculos de tiempo restante (event ya está definido aquí) ─────────────
  // `now` viene del hook useNow declarado arriba; tickea cada segundo.
  const msUntilStart = toSafeDate(event.startDate).getTime() - now;
  const msUntilRegOpen =
    toSafeDate(event.registrationConfig.startDate).getTime() - now;
  const msUntilRegClose =
    toSafeDate(event.registrationConfig.endDate).getTime() - now;
  const daysUntilStart = Math.ceil(msUntilStart / (1000 * 60 * 60 * 24));
  const daysUntilRegClose = Math.ceil(msUntilRegClose / (1000 * 60 * 60 * 24));

  /** Tiempo restante legible: "3 días 5h", "5h 30m", "2m 15s", "45s". */
  const formatTimeRemaining = (ms: number): string => {
    if (ms <= 0) return "";
    const totalSec = Math.floor(ms / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    if (days >= 1)
      return `${days} ${days === 1 ? "día" : "días"}${
        hours > 0 ? ` ${hours}h` : ""
      }`;
    if (hours >= 1) return `${hours}h${minutes > 0 ? ` ${minutes}m` : ""}`;
    if (minutes >= 1) return `${minutes}m${seconds > 0 ? ` ${seconds}s` : ""}`;
    return `${seconds}s`;
  };

  // =============================================
  // DIRECTOR VIEW: Vista específica para directores de club
  // =============================================
  if (isDirectorView && !isOrganizer) {
    const regWindow = (() => {
      const regStart = toSafeDate(event.registrationConfig?.startDate);
      const regEnd = toSafeDate(event.registrationConfig?.endDate);
      // Reapertura concedida a ESTE club: manda sobre las fechas del evento.
      // Va antes que las dos ramas de cierre para que no haya forma de que la
      // pantalla diga "cerradas" mientras el servidor sí lo deja inscribir.
      const reabierto =
        !!myClubId &&
        (event.registrationOpenClubIds ?? []).includes(myClubId);
      if (reabierto) {
        return {
          status: "reopened" as const,
          label: "Reabiertas para tu club",
          canRegister: true,
        };
      }
      if (now < regStart.getTime()) {
        return {
          status: "not_open" as const,
          label: `Abre en ${formatTimeRemaining(msUntilRegOpen)}`,
          canRegister: false,
        };
      }
      if (now > regEnd.getTime())
        return {
          status: "closed" as const,
          label: "Inscripciones cerradas",
          canRegister: false,
        };
      return {
        status: "open" as const,
        label: `Cierra en ${formatTimeRemaining(msUntilRegClose)}`,
        canRegister: true,
      };
    })();

    const MIN_FOR_RANKING = 10;
    const registeredCount = myActiveRegistrations.length;
    const pendingRegs = myClubRegistrations.filter(
      (r) => r.status === "pending",
    );
    const approvedRegs = myClubRegistrations.filter(
      (r) => r.status === "approved",
    );
    const rejectedRegs = myClubRegistrations.filter(
      (r) => r.status === "rejected",
    );
    const cancelledRegs = myClubRegistrations.filter(
      (r) => r.status === "cancelled",
    );
    const progress = Math.min((registeredCount / MIN_FOR_RANKING) * 100, 100);

    const approvedPayments =
      myClubPayments?.filter((p) => p.status === "approved") ?? [];
    const pendingPayments =
      myClubPayments?.filter((p) => p.status === "pending") ?? [];

    const approvedAmount = approvedPayments.reduce(
      (sum, p) => sum + p.amount,
      0,
    );
    const pendingAmount = pendingPayments.reduce((sum, p) => sum + p.amount, 0);
    const totalOwed = myActiveRegistrations.reduce(
      (sum, r) => sum + (r.unitPrice || 0),
      0,
    );
    const balance = totalOwed - approvedAmount;
    const paymentProgress =
      totalOwed > 0 ? Math.min((approvedAmount / totalOwed) * 100, 100) : 0;

    const isRanked = !!myClubRanking?.completedAt;
    const rankPosition = myClubRanking?.position;

    const daysUntilStart = Math.ceil(
      (toSafeDate(event.startDate).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24),
    );

    type DirectorTab =
      | "overview"
      | "members"
      | "submissions"
      | "payments"
      | "documents"
      | "ranking"
      | "agenda";
    const directorTabs: {
      id: DirectorTab;
      label: string;
      icon: React.ReactNode;
      count?: number;
    }[] = [
      {
        id: "overview",
        label: "Resumen",
        icon: <BarChart3 className="w-4 h-4" />,
      },
      {
        id: "members",
        label: "Mis Inscritos",
        icon: <Users className="w-4 h-4" />,
        count: registeredCount,
      },
      {
        id: "payments",
        label: "Mis Pagos",
        icon: <DollarSign className="w-4 h-4" />,
        count: myClubPayments?.length,
      },
      {
        id: "documents",
        label: "Documentos",
        icon: <FileText className="w-4 h-4" />,
      },
      {
        id: "submissions",
        label: "Entregables",
        icon: <FileCheck className="w-4 h-4" />,
        count: pendingSubmissions.length || undefined,
      },
      {
        id: "agenda",
        label: "Agenda",
        icon: <Calendar className="w-4 h-4" />,
      },
      { id: "ranking", label: "Ranking", icon: <Trophy className="w-4 h-4" /> },
    ];

    const getCategoryLabel = (category: string) => {
      const pricing = event?.pricing.find((p) => p.category === category);
      if (pricing?.label) return pricing.label;
      const defaults: Record<string, string> = {
        pathfinder: "Conquistador/Aventurero",
        staff: "Staff/Consejero",
        guest: "Invitado",
        family: "Familiar",
      };
      return defaults[category] || category;
    };

    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Modal director: cambiar tipo de entrada */}
        {event && editCategoryReg && (
          <EditCategoryModal
            isOpen={true}
            onClose={() => setEditCategoryReg(null)}
            registration={editCategoryReg}
            event={event}
            clubChurchId={myClubChurchId}
          />
        )}
        {/* Modal director: planilla oficial de inscripción */}
        {event && showPlanillaModal && (
          <PlanillaInscripcionModal
            isOpen={true}
            onClose={() => setShowPlanillaModal(false)}
            event={event}
            club={clubs?.[0]}
            registrations={myClubRegistrations}
            onMarkAllUniform={markAllUniform}
          />
        )}
        {event && swapReg && (
          <SwapRegistrationModal
            isOpen={true}
            onClose={() => setSwapReg(null)}
            event={event}
            outgoingRegistration={swapReg}
            clubName={swapReg.clubName ?? myClubName ?? ""}
            associationName={event.organizerName}
          />
        )}
        {/* Alerta de entregables pendientes */}
        {pendingSubmissions.length > 0 && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3 shadow-sm">
            <div className="shrink-0 w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
              <FileCheck className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-900">
                Tienes {pendingSubmissions.length}{" "}
                {pendingSubmissions.length === 1
                  ? "entregable pendiente"
                  : "entregables pendientes"}
              </p>
              <ul className="mt-1 text-sm text-amber-800 space-y-0.5">
                {pendingSubmissions.slice(0, 3).map((act) => {
                  const dl = act.submissionRequirements?.deadline;
                  const dlStr = (() => {
                    const d = toSubJsDate(dl);
                    return d ? d.toLocaleDateString("es-CO") : "";
                  })();
                  return (
                    <li key={act.id} className="truncate">
                      • {act.name}
                      {dlStr && (
                        <span className="text-xs text-amber-700 ml-1">
                          (cierra {dlStr})
                        </span>
                      )}
                    </li>
                  );
                })}
                {pendingSubmissions.length > 3 && (
                  <li className="text-xs italic">
                    y {pendingSubmissions.length - 3} más…
                  </li>
                )}
              </ul>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setActiveTab("submissions")}
              className="shrink-0"
            >
              Ir a entregables
            </Button>
          </div>
        )}
        {/* Header */}
        <div className="relative">
          {event.coverImageUrl ? (
            <div className="h-32 sm:h-44 rounded-xl overflow-hidden">
              <img
                src={event.coverImageUrl}
                alt={event.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent rounded-xl" />
            </div>
          ) : (
            <div className="h-32 sm:h-44 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-700" />
          )}
          <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6">
            <div className="flex items-end justify-between gap-2">
              <div className="flex items-start gap-2 sm:gap-4 min-w-0 flex-1">
                <button
                  onClick={() => navigate("/events")}
                  className="p-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-xl transition-colors text-white shrink-0"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                    <h1 className="text-lg sm:text-2xl font-bold text-white leading-tight">
                      {event.name}
                    </h1>
                    <Badge
                      className={`${getEventStatusColor(event.status)} shadow-lg shrink-0`}
                    >
                      {getEventStatusLabel(event.status)}
                    </Badge>
                  </div>
                  <p className="text-white/80 text-xs sm:text-base truncate">
                    {eventTypeLabels[event.eventType]} • {event.organizerName}
                  </p>
                </div>
              </div>
              {/* Director actions - desktop only */}
              <div className="hidden md:flex items-center gap-2 shrink-0">
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                    regWindow.status === "open"
                      ? "bg-green-500/90 text-white"
                      : regWindow.status === "reopened"
                        ? "bg-amber-500/90 text-white"
                        : regWindow.status === "not_open"
                          ? "bg-blue-500/90 text-white"
                          : "bg-red-500/80 text-white"
                  }`}
                >
                  {regWindow.canRegister ? (
                    <Clock className="w-3.5 h-3.5" />
                  ) : (
                    <Lock className="w-3.5 h-3.5" />
                  )}
                  {regWindow.label}
                </span>
                {regWindow.canRegister && (
                  <Button
                    onClick={() => setShowRegistrationModal(true)}
                    className="bg-white/20 backdrop-blur-sm border-white/30 text-white hover:bg-white/30"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Inscribir Miembros
                  </Button>
                )}
                {myActiveRegistrations.length > 0 && (
                  <Button
                    variant="secondary"
                    onClick={() => setShowPaymentUploadModal(true)}
                    className="bg-white/20 backdrop-blur-sm border-white/30 text-white hover:bg-white/30"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Subir Pago
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Director actions - mobile only */}
        <div className="md:hidden flex flex-wrap gap-2">
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
              regWindow.status === "open"
                ? "bg-green-100 text-green-700 border border-green-200"
                : regWindow.status === "reopened"
                  ? "bg-amber-100 text-amber-800 border border-amber-200"
                  : regWindow.status === "not_open"
                    ? "bg-blue-100 text-blue-700 border border-blue-200"
                    : "bg-red-100 text-red-700 border border-red-200"
            }`}
          >
            {regWindow.canRegister ? (
              <Clock className="w-3.5 h-3.5" />
            ) : (
              <Lock className="w-3.5 h-3.5" />
            )}
            {regWindow.label}
          </span>
          {regWindow.canRegister && (
            <Button
              size="sm"
              onClick={() => setShowRegistrationModal(true)}
              className="flex-1 min-w-[140px]"
            >
              <UserPlus className="w-4 h-4 mr-1.5" />
              Inscribir
            </Button>
          )}
          {myActiveRegistrations.length > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowPaymentUploadModal(true)}
              className="flex-1 min-w-[120px]"
            >
              <Upload className="w-4 h-4 mr-1.5" />
              Subir Pago
            </Button>
          )}
        </div>

        {/* Club Status Dashboard */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-4">
          <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div
                  className={`w-9 h-9 sm:w-12 sm:h-12 shrink-0 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md sm:shadow-lg ${registeredCount >= MIN_FOR_RANKING ? "bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/30" : "bg-gradient-to-br from-purple-400 to-purple-600 shadow-purple-500/30"} group-hover:scale-110 transition-transform duration-300`}
                >
                  <Users className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xl sm:text-3xl font-bold text-gray-900 leading-none truncate">
                    {registeredCount}
                  </p>
                  <p className="text-[10px] sm:text-xs font-medium text-gray-500 mt-0.5 sm:mt-1 truncate">
                    Inscritos
                  </p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <div className="flex-1 min-w-0 h-1 sm:h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${registeredCount >= MIN_FOR_RANKING ? "bg-emerald-500" : "bg-amber-400"}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-[9px] sm:text-[10px] text-gray-400 shrink-0">
                      {registeredCount}/{MIN_FOR_RANKING}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-12 sm:h-12 shrink-0 bg-gradient-to-br from-green-400 to-green-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md sm:shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
                  <UserCheck className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xl sm:text-3xl font-bold text-gray-900 leading-none truncate">
                    {approvedRegs.length}
                  </p>
                  <p className="text-[10px] sm:text-xs font-medium text-gray-500 mt-0.5 sm:mt-1 truncate">
                    Aprobados
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div
                  className={`w-9 h-9 sm:w-12 sm:h-12 shrink-0 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md sm:shadow-lg ${paymentProgress >= 100 ? "bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-emerald-500/30" : approvedAmount > 0 ? "bg-gradient-to-br from-blue-400 to-blue-600 shadow-blue-500/30" : "bg-gradient-to-br from-amber-400 to-amber-600 shadow-amber-500/30"} group-hover:scale-110 transition-transform duration-300`}
                >
                  <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm sm:text-lg font-bold text-gray-900 leading-tight truncate"
                    title={formatCurrency(approvedAmount)}
                  >
                    {formatCurrency(approvedAmount)}
                  </p>
                  <p
                    className="text-[10px] sm:text-[11px] font-medium text-gray-500 truncate"
                    title={`de ${formatCurrency(totalOwed)}`}
                  >
                    de {formatCurrency(totalOwed)}
                  </p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <div className="flex-1 min-w-0 h-1 sm:h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${paymentProgress >= 100 ? "bg-emerald-500" : paymentProgress > 0 ? "bg-blue-500" : "bg-gray-300"}`}
                        style={{ width: `${paymentProgress}%` }}
                      />
                    </div>
                    <span className="text-[9px] sm:text-[10px] text-gray-400 shrink-0">
                      {Math.round(paymentProgress)}%
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <button
            type="button"
            className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 rounded-xl bg-white text-left w-full"
            onClick={() => setActiveTab("ranking" as TabType)}
          >
            <div className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div
                  className={`w-9 h-9 sm:w-12 sm:h-12 shrink-0 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md sm:shadow-lg ${activityRankPosition != null ? "bg-gradient-to-br from-amber-400 to-amber-600 shadow-amber-500/30" : "bg-gradient-to-br from-gray-400 to-gray-600 shadow-gray-500/30"} group-hover:scale-110 transition-transform duration-300`}
                >
                  <Trophy className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xl sm:text-3xl font-bold text-gray-900 leading-none truncate">
                    {activityRankPosition != null
                      ? `#${activityRankPosition}`
                      : "—"}
                  </p>
                  <p className="text-[10px] sm:text-xs font-medium text-gray-500 mt-0.5 sm:mt-1 truncate">
                    Ranking actividades
                  </p>
                  {activityTotalPoints > 0 && (
                    <p className="text-[9px] font-semibold text-purple-600 leading-none mt-0.5">
                      {activityTotalPoints.toLocaleString()} pts
                    </p>
                  )}
                  {isRanked && rankPosition != null && (
                    <p className="text-[9px] text-gray-400 leading-none mt-0.5">
                      Inscripción: #{rankPosition}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </button>

          <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 col-span-2 md:col-span-1">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-12 sm:h-12 shrink-0 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md sm:shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                  <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xl sm:text-3xl font-bold text-gray-900 leading-none truncate">
                    {msUntilStart <= 0
                      ? "Pasado"
                      : msUntilStart < 24 * 60 * 60 * 1000
                        ? Math.max(
                            1,
                            Math.ceil(msUntilStart / (60 * 60 * 1000)),
                          )
                        : daysUntilStart}
                  </p>
                  <p className="text-[10px] sm:text-[11px] font-medium text-gray-500 mt-0.5 sm:mt-1 leading-tight">
                    {msUntilStart <= 0
                      ? ""
                      : msUntilStart < 24 * 60 * 60 * 1000
                        ? "horas para inicio"
                        : "días para inicio"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-md p-2">
          <div className="flex gap-2 overflow-x-auto">
            {directorTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id as TabType);
                  setConfirmCancelId(null);
                }}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg shadow-purple-500/30"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {tab.icon}
                {tab.label}
                {tab.count !== undefined && (
                  <Badge
                    className={`ml-1 ${activeTab === tab.id ? "bg-white/20 text-white" : "bg-gray-200 text-gray-700"}`}
                  >
                    {tab.count}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* === RESUMEN TAB === */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                {/* Descripción */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-slate-400 to-slate-600 rounded-xl flex items-center justify-center shadow-md shadow-slate-500/25">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      Descripción
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {event.description || "Sin descripción"}
                    </p>
                  </CardContent>
                </Card>

                {/* Fechas y Ubicación */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
                        <Calendar className="w-5 h-5 text-white" />
                      </div>
                      Detalles del Evento
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-100">
                        <Calendar className="w-5 h-5 text-purple-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">Inicio</p>
                          <p className="text-gray-600">
                            {formatDate(event.startDate)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatTime(event.startDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-100">
                        <Clock className="w-5 h-5 text-indigo-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">Fin</p>
                          <p className="text-gray-600">
                            {formatDate(event.endDate)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {formatTime(event.endDate)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-rose-50 to-red-50 rounded-xl border border-rose-100">
                      <MapPin className="w-5 h-5 text-rose-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">
                          {event.location.name}
                        </p>
                        <p className="text-gray-600">
                          {event.location.address}
                        </p>
                        <p className="text-sm text-gray-500">
                          {event.location.city}
                          {event.location.department &&
                            `, ${event.location.department}`}
                        </p>
                        {event.location.coordinates && (
                          <a
                            href={`https://maps.google.com/?q=${event.location.coordinates.lat},${event.location.coordinates.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-purple-600 hover:underline flex items-center gap-1 mt-2"
                          >
                            Ver en Google Maps{" "}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Precios */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-md shadow-emerald-500/25">
                        <DollarSign className="w-5 h-5 text-white" />
                      </div>
                      Precios
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {myClubIsDiscounted && (
                      <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                        Tu club aplica al precio con descuento
                        {event.discountLabel ? (
                          <strong> ({event.discountLabel})</strong>
                        ) : null}
                        .
                      </div>
                    )}
                    {(isAdmin || isOrganizer) &&
                      (event.discountedDistrictIds?.length ?? 0) > 0 && (
                        <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                          Descuento por distrito activo
                          {event.discountLabel ? (
                            <strong> ({event.discountLabel})</strong>
                          ) : null}{" "}
                          — aplica a {event.discountedDistrictIds!.length}{" "}
                          distrito(s).
                        </div>
                      )}
                    {event.pricing.map((price) => {
                      const hasEarlyBird =
                        price.earlyBirdAmount != null &&
                        price.earlyBirdDeadline;
                      const ebActive =
                        hasEarlyBird &&
                        new Date() <= toSafeDate(price.earlyBirdDeadline);
                      const hasDiscountedAmount =
                        price.discountedAmount != null;

                      // Vista admin/organizador: mostrar todos los valores
                      if (isAdmin || isOrganizer) {
                        return (
                          <div
                            key={price.category}
                            className="p-4 rounded-xl border bg-gray-50 border-gray-100 space-y-1.5"
                          >
                            <p className="font-medium text-gray-700 text-sm">
                              {price.label}
                            </p>
                            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                              <span className="text-xl font-bold text-gray-900">
                                {formatCurrency(price.amount)}
                              </span>
                              {hasEarlyBird && (
                                <span
                                  className={`text-sm font-semibold ${ebActive ? "text-green-700" : "text-gray-500"}`}
                                >
                                  Pronto pago:{" "}
                                  {formatCurrency(price.earlyBirdAmount!)}
                                  {ebActive && (
                                    <span className="ml-1 text-[10px] font-medium text-green-600">
                                      activo
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                            {hasDiscountedAmount && (
                              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 pt-1 border-t border-amber-100">
                                <span className="text-sm font-semibold text-amber-700">
                                  🏷️ {event.discountLabel ?? "Descuento"}:{" "}
                                  {formatCurrency(price.discountedAmount!)}
                                </span>
                                {price.discountedEarlyBirdAmount != null && (
                                  <span
                                    className={`text-xs font-semibold ${ebActive ? "text-amber-700" : "text-amber-500"}`}
                                  >
                                    + pronto pago:{" "}
                                    {formatCurrency(
                                      price.discountedEarlyBirdAmount,
                                    )}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Vista pública / director: precio efectivo personalizado
                      const useDiscount =
                        myClubIsDiscounted && hasDiscountedAmount;
                      const discountedEbActive =
                        useDiscount &&
                        ebActive &&
                        price.discountedEarlyBirdAmount != null;
                      const finalAmount = discountedEbActive
                        ? price.discountedEarlyBirdAmount!
                        : useDiscount
                          ? price.discountedAmount!
                          : ebActive
                            ? price.earlyBirdAmount!
                            : price.amount;
                      const strikedAmount = useDiscount
                        ? ebActive
                          ? (price.earlyBirdAmount ?? price.amount)
                          : price.amount
                        : ebActive
                          ? price.amount
                          : null;
                      const showGreen = useDiscount || ebActive;
                      return (
                        <div
                          key={price.category}
                          className={`p-4 rounded-xl border ${
                            showGreen
                              ? "bg-green-50 border-green-200"
                              : "bg-gray-50 border-gray-100"
                          }`}
                        >
                          <p className="font-medium text-gray-700 text-sm">
                            {price.label}
                          </p>
                          <p
                            className={`mt-1 text-xl font-bold ${
                              showGreen ? "text-green-700" : "text-gray-900"
                            }`}
                          >
                            {formatCurrency(finalAmount)}
                            {strikedAmount != null && (
                              <span className="line-through text-gray-400 text-sm font-normal ml-2">
                                {formatCurrency(strikedAmount)}
                              </span>
                            )}
                          </p>
                          {(useDiscount || ebActive) && (
                            <p className="text-[10px] text-green-600 font-medium mt-0.5">
                              {useDiscount && ebActive
                                ? `🏷️ Descuento + pronto pago`
                                : useDiscount
                                  ? `🏷️ Descuento ${event.discountLabel ?? ""}`.trim()
                                  : `🏷️ Pronto pago activo`}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Período de Inscripción */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      Inscripciones
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center p-2">
                      <span className="text-gray-600 text-sm">Inicio</span>
                      <span className="font-medium">
                        {formatShortDate(event.registrationConfig.startDate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-2">
                      <span className="text-gray-600 text-sm">Cierre</span>
                      <span className="font-medium">
                        {formatShortDate(event.registrationConfig.endDate)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Contacto */}
                {(event.contactName ||
                  event.contactEmail ||
                  event.contactPhone) && (
                  <Card className="border-0 shadow-lg">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-pink-600 rounded-xl flex items-center justify-center shadow-md shadow-pink-500/25">
                          <Mail className="w-5 h-5 text-white" />
                        </div>
                        Contacto
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {event.contactName && (
                        <p className="flex items-center gap-2 text-sm">
                          <Users className="w-4 h-4 text-gray-400" />
                          {event.contactName}
                        </p>
                      )}
                      {event.contactEmail && (
                        <p className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <a
                            href={`mailto:${event.contactEmail}`}
                            className="text-purple-600 hover:underline"
                          >
                            {event.contactEmail}
                          </a>
                        </p>
                      )}
                      {event.contactPhone && (
                        <p className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <a
                            href={`tel:${event.contactPhone}`}
                            className="text-purple-600 hover:underline"
                          >
                            {event.contactPhone}
                          </a>
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Clubes Permitidos */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Clubes Permitidos</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {event.clubTypesAllowed.map((type) => (
                        <SectionBadge
                          key={type}
                          section={type as ClubType}
                          size="sm"
                          variant="soft"
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Ranking de actividades embebido en el resumen */}
            <EventRankingTab
              event={event}
              registrations={registrations ?? []}
              isAdmin={false}
            />
          </div>
        )}

        {/* === MIS INSCRITOS TAB === */}
        {activeTab === "members" && (
          <div className="space-y-4">
            {/* Quick stats */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-white rounded-xl shadow-md">
              <div className="flex items-center gap-x-4 gap-y-2 flex-wrap text-sm">
                <span className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-purple-500" />
                  <strong>{registeredCount}</strong> inscritos
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <strong className="text-green-700">
                    {approvedRegs.length}
                  </strong>{" "}
                  aprobados
                </span>
                {pendingRegs.length > 0 && (
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <strong className="text-amber-700">
                      {pendingRegs.length}
                    </strong>{" "}
                    pendientes
                  </span>
                )}
                {rejectedRegs.length > 0 && (
                  <span className="flex items-center gap-1.5">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <strong className="text-red-700">
                      {rejectedRegs.length}
                    </strong>{" "}
                    rechazados
                  </span>
                )}
                {cancelledRegs.length > 0 && (
                  <span className="flex items-center gap-1.5">
                    <XCircle className="w-4 h-4 text-gray-400" />
                    <strong className="text-gray-600">
                      {cancelledRegs.length}
                    </strong>{" "}
                    cancelados
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-sm font-medium text-gray-700">
                  <DollarSign className="w-4 h-4 inline text-emerald-500" />
                  {formatCurrency(
                    myActiveRegistrations.reduce(
                      (sum, r) => sum + (r.unitPrice || 0),
                      0,
                    ),
                  )}
                </span>
                {myActiveRegistrations.length > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setShowPlanillaModal(true)}
                    className="shrink-0"
                    title="Descargar la planilla general de inscripción (Excel o PDF)"
                  >
                    <ClipboardCheck className="w-4 h-4 mr-1.5" />
                    <span className="hidden sm:inline">Planilla</span>
                  </Button>
                )}
                {regWindow.canRegister && (
                  <Button
                    size="sm"
                    onClick={() => setShowRegistrationModal(true)}
                    className="ml-auto shrink-0"
                  >
                    <UserPlus className="w-4 h-4 mr-1.5" />
                    <span className="hidden sm:inline">Inscribir más</span>
                    <span className="sm:hidden">Inscribir</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Members table */}
            {myActiveRegistrations.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900">
                    No hay miembros inscritos
                  </h3>
                  <p className="text-gray-500 mt-1 text-sm">
                    Inscribe miembros de tu club para participar en este evento
                  </p>
                  {regWindow.canRegister && (
                    <Button
                      onClick={() => setShowRegistrationModal(true)}
                      className="mt-4"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Inscribir Miembros
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow-lg overflow-hidden">
                {/* Toolbar de filtros */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 border-b bg-gray-50/60">
                  <div className="relative flex-1 min-w-0">
                    <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      value={dirSearch}
                      onChange={(e) => setDirSearch(e.target.value)}
                      placeholder="Buscar por nombre…"
                      className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <select
                    value={dirStatusFilter}
                    onChange={(e) =>
                      setDirStatusFilter(
                        e.target.value as typeof dirStatusFilter,
                      )
                    }
                    className="text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="all">Todos los estados</option>
                    <option value="pending">Pendiente</option>
                    <option value="approved">Aprobado</option>
                    <option value="waitlist">Lista de espera</option>
                  </select>
                  {dirAvailableCategories.length > 1 && (
                    <select
                      value={dirCategoryFilter}
                      onChange={(e) => setDirCategoryFilter(e.target.value)}
                      className="text-sm border border-gray-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="all">Todas las categorías</option>
                      {dirAvailableCategories.map((cat) => (
                        <option key={cat} value={cat}>
                          {getCategoryLabel(cat)}
                        </option>
                      ))}
                    </select>
                  )}
                  {(dirSearch ||
                    dirStatusFilter !== "all" ||
                    dirCategoryFilter !== "all") && (
                    <button
                      type="button"
                      onClick={() => {
                        setDirSearch("");
                        setDirStatusFilter("all");
                        setDirCategoryFilter("all");
                      }}
                      className="text-xs text-purple-600 hover:text-purple-800 hover:underline px-2"
                    >
                      Limpiar
                    </button>
                  )}
                  <span className="text-xs text-gray-500 sm:ml-auto whitespace-nowrap">
                    {displayedRegistrations.length} de{" "}
                    {myActiveRegistrations.length}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b bg-gray-50">
                        <th className="px-4 py-3 font-medium">
                          <button
                            type="button"
                            onClick={() => toggleDirSort("name")}
                            className="inline-flex items-center gap-1 hover:text-gray-900"
                          >
                            Miembro
                            {dirSortKey === "name" &&
                              (dirSortDir === "asc" ? (
                                <ChevronUp className="w-3 h-3" />
                              ) : (
                                <ChevronDown className="w-3 h-3" />
                              ))}
                          </button>
                        </th>
                        <th className="px-4 py-3 font-medium">
                          <button
                            type="button"
                            onClick={() => toggleDirSort("category")}
                            className="inline-flex items-center gap-1 hover:text-gray-900"
                          >
                            Categoría
                            {dirSortKey === "category" &&
                              (dirSortDir === "asc" ? (
                                <ChevronUp className="w-3 h-3" />
                              ) : (
                                <ChevronDown className="w-3 h-3" />
                              ))}
                          </button>
                        </th>
                        <th className="px-4 py-3 font-medium">
                          <button
                            type="button"
                            onClick={() => toggleDirSort("status")}
                            className="inline-flex items-center gap-1 hover:text-gray-900"
                          >
                            Estado
                            {dirSortKey === "status" &&
                              (dirSortDir === "asc" ? (
                                <ChevronUp className="w-3 h-3" />
                              ) : (
                                <ChevronDown className="w-3 h-3" />
                              ))}
                          </button>
                        </th>
                        <th
                          className="px-4 py-3 font-medium text-center"
                          title="Aventureros que asisten con uniforme (sección D de la planilla)"
                        >
                          Uniforme
                        </th>
                        <th className="px-4 py-3 font-medium">Check-in</th>
                        <th className="px-4 py-3 font-medium text-right">
                          <button
                            type="button"
                            onClick={() => toggleDirSort("price")}
                            className="inline-flex items-center gap-1 hover:text-gray-900"
                          >
                            Precio
                            {dirSortKey === "price" &&
                              (dirSortDir === "asc" ? (
                                <ChevronUp className="w-3 h-3" />
                              ) : (
                                <ChevronDown className="w-3 h-3" />
                              ))}
                          </button>
                        </th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {displayedRegistrations.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-4 py-8 text-center text-sm text-gray-500"
                          >
                            No hay inscripciones que coincidan con los filtros.
                          </td>
                        </tr>
                      ) : (
                        displayedRegistrations.map((reg) => (
                          <tr
                            key={reg.id}
                            className={`border-b last:border-b-0 transition-colors ${confirmCancelId === reg.id ? "bg-red-50" : reg.status === "pending" ? "bg-amber-50/30 hover:bg-amber-50" : "hover:bg-gray-50"}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center shadow-sm">
                                  <span className="text-[10px] font-bold text-white">
                                    {reg.personName
                                      ?.split(" ")
                                      .map((n) => n[0])
                                      .join("")
                                      .slice(0, 2)
                                      .toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {reg.personName}
                                  </p>
                                  {reg.medicalNotes && (
                                    <p className="text-xs text-red-600">
                                      🏥 {reg.medicalNotes}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              <div className="flex items-center gap-2">
                                <span>{getCategoryLabel(reg.category)}</span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEditCategoryReg(reg);
                                  }}
                                  className="text-xs text-purple-600 hover:text-purple-800 hover:underline font-medium px-2 py-0.5 rounded border border-purple-200 hover:bg-purple-50"
                                  title="Cambiar tipo de entrada"
                                >
                                  Cambiar
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge
                                className={
                                  reg.status === "approved"
                                    ? "bg-green-100 text-green-700"
                                    : reg.status === "pending"
                                      ? "bg-yellow-100 text-yellow-700"
                                      : reg.status === "waitlist"
                                        ? "bg-blue-100 text-blue-700"
                                        : "bg-gray-100 text-gray-700"
                                }
                              >
                                {reg.status === "approved"
                                  ? "Aprobado"
                                  : reg.status === "pending"
                                    ? "Pendiente"
                                    : reg.status === "waitlist"
                                      ? "Lista espera"
                                      : reg.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {isParticipantCategory(reg.category) ? (
                                <input
                                  type="checkbox"
                                  checked={reg.hasUniform === true}
                                  onChange={(e) =>
                                    toggleUniform(reg, e.target.checked)
                                  }
                                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                                  title={
                                    reg.hasUniform === true
                                      ? "Asiste con uniforme"
                                      : "Sin uniforme: sale como aventurero amigo en la planilla"
                                  }
                                />
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <ConsentStatusBadge
                                  progress={consentProgressFor(reg.memberId)}
                                />
                                {(() => {
                                  const c = consentByMember(reg.memberId);
                                  const prog = consentProgressFor(reg.memberId);
                                  return (
                                    <>
                                      <DirectorConsentLauncher
                                        event={event!}
                                        registration={reg}
                                        clubName={
                                          myClubName ?? reg.clubName ?? ""
                                        }
                                        associationName={
                                          event?.organizerName ?? "Asociación"
                                        }
                                        label={
                                          prog.completed
                                            ? "Re-abrir"
                                            : prog.doneCount > 0
                                              ? "Continuar"
                                              : "Iniciar"
                                        }
                                        variant="ghost"
                                      />
                                      {c && (
                                        <button
                                          onClick={() =>
                                            setDocsModal({
                                              consent: c,
                                              memberName: reg.personName,
                                            })
                                          }
                                          title="Ver documentos"
                                          className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-50"
                                        >
                                          <FileText className="w-4 h-4" />
                                        </button>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right font-medium">
                              {formatCurrency(reg.unitPrice)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {confirmCancelId === reg.id ? (
                                <div className="flex items-center justify-end gap-1.5">
                                  <span className="text-xs text-red-600 font-medium mr-1">
                                    {reg.paymentStatus === "paid"
                                      ? "⚠️ Ya pagó. ¿Cancelar?"
                                      : "¿Cancelar?"}
                                  </span>
                                  <button
                                    onClick={() => {
                                      cancelRegistration.mutate({
                                        id: reg.id,
                                        eventId: event!.id,
                                      });
                                      setConfirmCancelId(null);
                                    }}
                                    disabled={cancelRegistration.isPending}
                                    className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded-md font-medium transition-colors disabled:opacity-50"
                                  >
                                    Sí
                                  </button>
                                  <button
                                    onClick={() => setConfirmCancelId(null)}
                                    className="px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium transition-colors"
                                  >
                                    No
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => setSwapReg(reg)}
                                    title="Intercambiar por otro integrante"
                                    className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                  >
                                    <ArrowRightLeft className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setConfirmCancelId(reg.id)}
                                    title="Cancelar inscripción"
                                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Summary */}
                <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between text-sm text-gray-600">
                  <span>
                    Total:{" "}
                    {formatCurrency(
                      myActiveRegistrations.reduce(
                        (sum, r) => sum + (r.unitPrice || 0),
                        0,
                      ),
                    )}
                  </span>
                  <span>
                    {approvedRegs.length} aprobados de {registeredCount}
                  </span>
                </div>
              </Card>
            )}

            {/* Rejected members */}
            {rejectedRegs.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-red-600 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Rechazados ({rejectedRegs.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="divide-y">
                    {rejectedRegs.map((reg) => (
                      <div
                        key={reg.id}
                        className="flex items-center justify-between py-2 text-sm opacity-70"
                      >
                        <span className="text-gray-600">{reg.personName}</span>
                        <Badge className="bg-red-100 text-red-700 text-[10px]">
                          Rechazado
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cancelled members */}
            {cancelledRegs.length > 0 && (
              <Card className="border-0 shadow-md border-gray-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-500 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Cancelados ({cancelledRegs.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="divide-y">
                    {cancelledRegs.map((reg) => (
                      <div
                        key={reg.id}
                        className="flex items-center justify-between py-2 text-sm opacity-60"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">
                            {reg.personName}
                          </span>
                          <Badge className="bg-gray-100 text-gray-500 text-[10px]">
                            {getCategoryLabel(reg.category)}
                          </Badge>
                        </div>
                        {regWindow.canRegister && (
                          <button
                            onClick={() => {
                              updateRegistrationStatus.mutate({
                                id: reg.id,
                                status: "pending",
                                eventId: event!.id,
                              });
                            }}
                            disabled={updateRegistrationStatus.isPending}
                            className="text-xs text-purple-600 hover:text-purple-800 font-medium hover:underline disabled:opacity-50"
                          >
                            Reinscribir
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* === MIS PAGOS TAB === */}
        {activeTab === "payments" && (
          <div className="space-y-6">
            {/* Payment progress bar */}
            {totalOwed > 0 && (
              <Card className="border-0 shadow-lg overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">
                      Avance de pagos
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                      {Math.round(paymentProgress)}%
                    </p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${paymentProgress >= 100 ? "bg-gradient-to-r from-emerald-400 to-emerald-600" : paymentProgress > 50 ? "bg-gradient-to-r from-blue-400 to-blue-600" : "bg-gradient-to-r from-amber-400 to-amber-600"}`}
                      style={{ width: `${paymentProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatCurrency(approvedAmount)} aprobado de{" "}
                    {formatCurrency(totalOwed)} total
                  </p>
                </CardContent>
              </Card>
            )}
            {/* Payment stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Total a Pagar</p>
                  <p
                    className="text-xl font-bold text-gray-900 truncate tabular-nums"
                    title={formatCurrency(totalOwed)}
                  >
                    {formatCurrency(totalOwed)}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {myActiveRegistrations.length} inscrito
                    {myActiveRegistrations.length !== 1 ? "s" : ""}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Aprobado</p>
                  <p
                    className="text-xl font-bold text-emerald-600 truncate tabular-nums"
                    title={formatCurrency(approvedAmount)}
                  >
                    {formatCurrency(approvedAmount)}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {approvedPayments.length} pago
                    {approvedPayments.length !== 1 ? "s" : ""}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Saldo Pendiente</p>
                  <p
                    className={`text-xl font-bold truncate tabular-nums ${balance > 0 ? "text-amber-600" : "text-emerald-600"}`}
                    title={formatCurrency(Math.max(balance, 0))}
                  >
                    {formatCurrency(Math.max(balance, 0))}
                  </p>
                  {balance <= 0 && totalOwed > 0 && (
                    <p className="text-[10px] text-emerald-500">✓ Cubierto</p>
                  )}
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg">
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">En Revisión</p>
                  <p
                    className="text-xl font-bold text-blue-600 truncate tabular-nums"
                    title={formatCurrency(pendingAmount)}
                  >
                    {formatCurrency(pendingAmount)}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {pendingPayments.length} pago
                    {pendingPayments.length !== 1 ? "s" : ""} por verificar
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Payment list */}
            {!myClubPayments || myClubPayments.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="py-12 text-center">
                  <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900">
                    No hay pagos registrados
                  </h3>
                  <p className="text-gray-500 mt-1 text-sm">
                    Sube un comprobante de pago para tu club
                  </p>
                  {myActiveRegistrations.length > 0 && (
                    <Button
                      onClick={() => setShowPaymentUploadModal(true)}
                      className="mt-4"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Subir Pago
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Historial de Pagos</CardTitle>
                    {myActiveRegistrations.length > 0 && (
                      <Button
                        size="sm"
                        onClick={() => setShowPaymentUploadModal(true)}
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Nuevo Pago
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {myClubPayments.map((payment) => (
                      <div
                        key={payment.id}
                        className={`p-4 rounded-xl border-2 ${
                          payment.status === "approved"
                            ? "border-emerald-200 bg-emerald-50/50"
                            : payment.status === "rejected"
                              ? "border-red-200 bg-red-50/50"
                              : "border-gray-200 bg-white"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-bold text-gray-900 tabular-nums">
                              {formatCurrency(payment.amount)}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {toSafeDate(
                                payment.uploadedAt,
                              ).toLocaleDateString("es-CO", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                              {payment.description &&
                                ` · ${payment.description}`}
                            </p>
                          </div>
                          <Badge
                            className={`px-3 py-1 ${
                              payment.status === "approved"
                                ? "bg-emerald-100 text-emerald-700"
                                : payment.status === "pending"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-red-100 text-red-700"
                            }`}
                          >
                            {payment.status === "approved"
                              ? "✓ Aprobado"
                              : payment.status === "pending"
                                ? "⏳ Pendiente"
                                : "✗ Rechazado"}
                          </Badge>
                        </div>
                        {payment.status === "rejected" &&
                          payment.rejectionReason && (
                            <div className="mt-2 px-3 py-2 bg-red-50 rounded-lg border border-red-100">
                              <p className="text-xs text-red-600 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {payment.rejectionReason}
                              </p>
                            </div>
                          )}
                        {payment.status === "approved" &&
                          payment.validatedByName && (
                            <p className="text-[10px] text-gray-400 mt-1">
                              Aprobado por {payment.validatedByName}
                            </p>
                          )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* === RANKING TAB === */}
        {activeTab === "ranking" && (
          <EventRankingTab
            event={event}
            registrations={registrations ?? []}
            isAdmin={false}
          />
        )}

        {/* === DOCUMENTS TAB === */}
        {activeTab === "documents" && myClubId && myClubName && (
          <Card>
            <CardContent className="p-6">
              <EventClubAttachmentsPanel
                event={{ id: event.id, name: event.name }}
                clubId={myClubId}
                clubName={myClubName}
                canEdit
              />
            </CardContent>
          </Card>
        )}

        {/* === ENTREGABLES (Director) === */}
        {activeTab === "submissions" && (
          <EventActivitiesTab
            event={event}
            registrations={registrations ?? []}
            isAdmin={false}
            initialView="submissions"
            initialActivityId={hashActivityId ?? undefined}
          />
        )}

        {/* === AGENDA (Director / lectura) === */}
        {activeTab === "agenda" && (
          <EventAgendaPanel event={event} canEdit={false} />
        )}

        {/* Modals */}
        {myClubId && myClubName && (
          <EventRegistrationModal
            isOpen={showRegistrationModal}
            onClose={() => setShowRegistrationModal(false)}
            onSuccess={() => setShowPaymentUploadModal(true)}
            event={event}
            clubId={myClubId}
            clubName={myClubName}
            clubChurchId={myClubChurchId}
            registeredPersonIds={registeredPersonIds}
            currentClubRegisteredCount={registeredCount}
          />
        )}

        {myClubId && myClubName && (
          <PaymentUploadModal
            isOpen={showPaymentUploadModal}
            onClose={() => setShowPaymentUploadModal(false)}
            event={event}
            clubId={myClubId}
            clubName={myClubName}
            expectedAmount={totalOwed}
            registrationsCount={registeredCount}
          />
        )}

        {docsModal && (
          <MemberConsentDocsModal
            isOpen
            onClose={() => setDocsModal(null)}
            consent={docsModal.consent}
            memberName={docsModal.memberName}
          />
        )}
      </div>
    );
  }

  const statusActions: Record<
    EventStatus,
    {
      status: EventStatus;
      label: string;
      icon: React.ReactNode;
      variant: "primary" | "secondary" | "danger";
    }[]
  > = {
    draft: [
      {
        status: "published",
        label: "Publicar",
        icon: <Send className="w-4 h-4" />,
        variant: "primary",
      },
      {
        status: "cancelled",
        label: "Cancelar",
        icon: <XCircle className="w-4 h-4" />,
        variant: "danger",
      },
    ],
    published: [
      {
        status: "registration_closed",
        label: "Cerrar Inscripciones",
        icon: <Square className="w-4 h-4" />,
        variant: "secondary",
      },
      {
        status: "cancelled",
        label: "Cancelar",
        icon: <XCircle className="w-4 h-4" />,
        variant: "danger",
      },
    ],
    registration_closed: [
      {
        status: "in_progress",
        label: "Iniciar Evento",
        icon: <Play className="w-4 h-4" />,
        variant: "primary",
      },
      {
        status: "published",
        label: "Reabrir Inscripciones",
        icon: <Send className="w-4 h-4" />,
        variant: "secondary",
      },
    ],
    in_progress: [
      {
        status: "completed",
        label: "Finalizar",
        icon: <CheckCircle className="w-4 h-4" />,
        variant: "primary",
      },
    ],
    completed: [],
    cancelled: [
      {
        status: "draft",
        label: "Reactivar",
        icon: <Edit className="w-4 h-4" />,
        variant: "secondary",
      },
    ],
  };

  const tabs = [
    {
      id: "overview" as TabType,
      label: "Resumen",
      icon: <BarChart3 className="w-4 h-4" />,
    },
    {
      id: "registrations" as TabType,
      label: "Inscripciones",
      icon: <ClipboardList className="w-4 h-4" />,
      count: stats.total,
    },
    {
      id: "clubs" as TabType,
      label: "Clubes",
      icon: <Building2 className="w-4 h-4" />,
      count: stats.byClub.length,
    },
    {
      id: "ranking" as TabType,
      label: "Ranking",
      icon: <Trophy className="w-4 h-4" />,
    },
    {
      id: "activities" as TabType,
      label: "Actividades",
      icon: <Activity className="w-4 h-4" />,
    },
    {
      id: "agenda" as TabType,
      label: "Agenda",
      icon: <Calendar className="w-4 h-4" />,
    },
    {
      id: "payments" as TabType,
      label: "Pagos",
      icon: <DollarSign className="w-4 h-4" />,
    },
    {
      id: "settings" as TabType,
      label: "Configuración",
      icon: <Settings className="w-4 h-4" />,
    },
  ];

  // Calcular días restantes

  return (
    <div className="space-y-6">
      <EventConsentGate eventId={event.id} nonBlocking />
      <Breadcrumbs
        items={[{ label: "Eventos", to: "/events" }, { label: event.name }]}
      />
      {/* Header con imagen de fondo */}
      <div className="relative">
        {event.coverImageUrl ? (
          <div className="h-32 sm:h-48 rounded-xl overflow-hidden">
            <img
              src={event.coverImageUrl}
              alt={event.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent rounded-xl" />
          </div>
        ) : (
          <div className="h-32 sm:h-48 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-700" />
        )}

        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div className="flex items-start gap-3 sm:gap-4 min-w-0">
              <button
                onClick={() => navigate("/events")}
                className="p-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-xl transition-colors duration-200 text-white shrink-0"
                aria-label="Volver a eventos"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <div className="flex items-center gap-2 sm:gap-3 mb-1 flex-wrap">
                  <h1 className="text-lg sm:text-2xl font-bold text-white truncate max-w-full">
                    {event.name}
                  </h1>
                  <Badge
                    className={`${getEventStatusColor(event.status)} shadow-lg`}
                  >
                    {getEventStatusLabel(event.status)}
                  </Badge>
                </div>
                <p className="text-white/80 text-xs sm:text-base truncate">
                  {eventTypeLabels[event.eventType]} • {event.organizerName}
                </p>
              </div>
            </div>

            {isOrganizer && (
              <div className="flex items-center gap-2 flex-wrap">
                <Can permission="events.write">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowEditModal(true)}
                    className="bg-white/20 backdrop-blur-sm border-white/30 text-white hover:bg-white/30 transition-all duration-300"
                    aria-label="Editar evento"
                  >
                    <Edit className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Editar</span>
                  </Button>
                </Can>
                <Can permission="events.approve">
                  {statusActions[event.status].map((action) => (
                    <Button
                      key={action.status}
                      variant={action.variant}
                      size="sm"
                      onClick={() => handleStatusChange(action.status)}
                      disabled={updateStatusMutation.isPending}
                      aria-label={action.label}
                    >
                      {action.icon}
                      <span className="ml-1 sm:ml-2 hidden sm:inline">
                        {action.label}
                      </span>
                    </Button>
                  ))}
                </Can>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats Bar - Moderno */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 shrink-0 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xl sm:text-3xl font-bold text-gray-900 leading-none truncate">
                  {stats.total}
                </p>
                <p className="text-xs font-medium text-gray-500 mt-1 truncate">
                  Inscripciones
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 shrink-0 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
                <UserCheck className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xl sm:text-3xl font-bold text-gray-900 leading-none truncate">
                  {stats.approved}
                </p>
                <p className="text-xs font-medium text-gray-500 mt-1 truncate">
                  Aprobados
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 shrink-0 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform duration-300">
                <Hourglass className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xl sm:text-3xl font-bold text-gray-900 leading-none truncate">
                  {stats.pending}
                </p>
                <p className="text-xs font-medium text-gray-500 mt-1 truncate">
                  Pendientes
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 shrink-0 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-110 transition-transform duration-300">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-lg font-bold text-gray-900 leading-tight truncate"
                  title={formatCurrency(stats.paidAmount)}
                >
                  {formatCurrency(stats.paidAmount)}
                </p>
                <p
                  className="text-[11px] font-medium text-gray-500 truncate"
                  title={`de ${formatCurrency(stats.totalAmount)}`}
                >
                  de {formatCurrency(stats.totalAmount)}
                </p>
                {stats.totalAmount > 0 && (
                  <div className="mt-1 flex items-center gap-1.5">
                    <div className="flex-1 min-w-0 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${stats.paidAmount >= stats.totalAmount ? "bg-emerald-500" : stats.paidAmount > 0 ? "bg-blue-500" : "bg-gray-300"}`}
                        style={{
                          width: `${Math.min((stats.paidAmount / stats.totalAmount) * 100, 100)}%`,
                        }}
                      />
                    </div>
                    <span className="text-[10px] text-gray-400 shrink-0">
                      {Math.round((stats.paidAmount / stats.totalAmount) * 100)}
                      %
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 shrink-0 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xl sm:text-3xl font-bold text-gray-900 leading-none truncate">
                  {msUntilStart <= 0
                    ? "Pasado"
                    : msUntilStart < 24 * 60 * 60 * 1000
                      ? Math.max(1, Math.ceil(msUntilStart / (60 * 60 * 1000)))
                      : daysUntilStart}
                </p>
                <p className="text-[11px] font-medium text-gray-500 mt-1 leading-tight">
                  {msUntilStart <= 0
                    ? ""
                    : msUntilStart < 24 * 60 * 60 * 1000
                      ? "horas para inicio"
                      : "días para inicio"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Modernos */}
      <div className="bg-white rounded-2xl shadow-md p-2">
        <div className="flex gap-1 sm:gap-2 overflow-x-auto -mx-1 px-1 pb-1 sm:pb-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-xl text-sm sm:text-base font-medium transition-all duration-300 whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg shadow-purple-500/30"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.count !== undefined && (
                <Badge
                  className={`ml-1 ${activeTab === tab.id ? "bg-white/20 text-white" : "bg-gray-200 text-gray-700"}`}
                >
                  {tab.count}
                </Badge>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Descripción */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-slate-400 to-slate-600 rounded-xl flex items-center justify-center shadow-md shadow-slate-500/25">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  Descripción
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {event.description || "Sin descripción"}
                </p>
              </CardContent>
            </Card>

            {/* Fechas y Ubicación */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  Detalles del Evento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-100">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        Fecha de Inicio
                      </p>
                      <p className="text-gray-600">
                        {formatDate(event.startDate)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatTime(event.startDate)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-100">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-md shadow-indigo-500/25">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Fecha de Fin</p>
                      <p className="text-gray-600">
                        {formatDate(event.endDate)}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatTime(event.endDate)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-start gap-3 p-4 bg-gradient-to-br from-rose-50 to-red-50 rounded-xl border border-rose-100">
                    <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-rose-600 rounded-xl flex items-center justify-center shadow-md shadow-rose-500/25">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {event.location.name}
                      </p>
                      <p className="text-gray-600">{event.location.address}</p>
                      <p className="text-sm text-gray-500">
                        {event.location.city}
                        {event.location.department &&
                          `, ${event.location.department}`}
                      </p>
                      {event.location.coordinates && (
                        <a
                          href={`https://maps.google.com/?q=${event.location.coordinates.lat},${event.location.coordinates.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-purple-600 hover:underline flex items-center gap-1 mt-2"
                        >
                          Ver en Google Maps{" "}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Inscripciones por Club */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-md shadow-indigo-500/25">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  Inscripciones por Club ({stats.byClub.length} clubes)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.byClub.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">
                    No hay inscripciones aún
                  </p>
                ) : (
                  <div className="space-y-2">
                    {stats.byClub.map((club) => (
                      <div
                        key={club.clubId}
                        className="border-2 border-gray-100 rounded-xl overflow-hidden hover:border-gray-200 transition-colors duration-200"
                      >
                        <button
                          onClick={() => toggleClubExpand(club.clubId)}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors duration-200"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
                              <Building2 className="w-5 h-5 text-white" />
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-gray-900">
                                {club.clubName}
                              </p>
                              <p className="text-sm text-gray-500">
                                {club.count} inscrito
                                {club.count !== 1 ? "s" : ""} •{" "}
                                {formatCurrency(club.amount)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-green-100 text-green-700">
                                {club.approved} ✓
                              </Badge>
                              <Badge className="bg-yellow-100 text-yellow-700">
                                {club.pending} ⏳
                              </Badge>
                              <Badge className="bg-emerald-100 text-emerald-700">
                                {club.paid} 💰
                              </Badge>
                            </div>
                            {expandedClubs.has(club.clubId) ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </button>

                        {expandedClubs.has(club.clubId) && (
                          <div className="border-t bg-gray-50 p-4 overflow-x-auto">
                            <table className="w-full text-sm min-w-[520px]">
                              <thead>
                                <tr className="text-left text-gray-500">
                                  <th className="pb-2">Persona</th>
                                  <th className="pb-2">Categoría</th>
                                  <th className="pb-2">Estado</th>
                                  <th className="pb-2 text-right">Monto</th>
                                  {isAdmin && <th className="pb-2" />}
                                </tr>
                              </thead>
                              <tbody>
                                {registrationsByClub
                                  .get(club.clubId)
                                  ?.map((reg) => (
                                    <tr
                                      key={reg.id}
                                      className="border-t border-gray-200"
                                    >
                                      <td className="py-2 font-medium">
                                        {reg.personName}
                                      </td>
                                      <td className="py-2 text-gray-600">
                                        {getCategoryLabel(reg.category)}
                                      </td>
                                      <td className="py-2">
                                        <Badge
                                          className={
                                            reg.status === "approved"
                                              ? "bg-green-100 text-green-700"
                                              : reg.status === "pending"
                                                ? "bg-yellow-100 text-yellow-700"
                                                : reg.status === "rejected"
                                                  ? "bg-red-100 text-red-700"
                                                  : "bg-gray-100 text-gray-700"
                                          }
                                        >
                                          {reg.status}
                                        </Badge>
                                      </td>
                                      <td className="py-2 text-right">
                                        {formatCurrency(reg.unitPrice)}
                                      </td>
                                      {isAdmin && (
                                        <td className="py-2 text-right pl-2">
                                          <button
                                            type="button"
                                            onClick={() => setAdminEditReg(reg)}
                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50 rounded border border-purple-200"
                                            title="Editar inscripción (admin)"
                                          >
                                            <Pencil className="w-3 h-3" />
                                            Editar
                                          </button>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Precios */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-md shadow-emerald-500/25">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  Precios
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {event.pricing.map((price) => (
                  <div
                    key={price.category}
                    className="flex items-center justify-between p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-100"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{price.label}</p>
                      {price.earlyBirdAmount && price.earlyBirdDeadline && (
                        <p className="text-xs text-green-600 font-medium">
                          Pronto pago: {formatCurrency(price.earlyBirdAmount)}
                        </p>
                      )}
                    </div>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(price.amount)}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Fechas de Inscripción */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  Período de Inscripción
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-2">
                  <span className="text-gray-600">Inicio</span>
                  <span className="font-medium">
                    {formatShortDate(event.registrationConfig.startDate)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-2">
                  <span className="text-gray-600">Cierre</span>
                  <span className="font-medium">
                    {formatShortDate(event.registrationConfig.endDate)}
                  </span>
                </div>
                {daysUntilRegClose > 0 && event.status === "published" && (
                  <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl text-center border border-amber-200">
                    <p className="text-sm text-amber-800 font-medium">
                      <strong>{formatTimeRemaining(msUntilRegClose)}</strong>{" "}
                      para cierre de inscripciones
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contacto */}
            {(event.contactName ||
              event.contactEmail ||
              event.contactPhone) && (
              <Card className="border-0 shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-pink-600 rounded-xl flex items-center justify-center shadow-md shadow-pink-500/25">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    Contacto
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {event.contactName && (
                    <div className="flex items-center gap-3 p-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span>{event.contactName}</span>
                    </div>
                  )}
                  {event.contactEmail && (
                    <div className="flex items-center gap-3 p-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <a
                        href={`mailto:${event.contactEmail}`}
                        className="text-purple-600 hover:underline"
                      >
                        {event.contactEmail}
                      </a>
                    </div>
                  )}
                  {event.contactPhone && (
                    <div className="flex items-center gap-3 p-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a
                        href={`tel:${event.contactPhone}`}
                        className="text-purple-600 hover:underline"
                      >
                        {event.contactPhone}
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Tipos de Club Permitidos */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-violet-400 to-violet-600 rounded-xl flex items-center justify-center shadow-md shadow-violet-500/25">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  Clubes Permitidos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {event.clubTypesAllowed.map((type) => (
                    <SectionBadge
                      key={type}
                      section={type as ClubType}
                      size="sm"
                      variant="soft"
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Preview de Agenda en Resumen */}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setActiveTab("agenda")}
              className="w-full text-left rounded-xl border-2 border-dashed border-purple-200 hover:border-purple-400 hover:bg-purple-50/50 p-4 flex items-center justify-between transition-colors"
              title="Ver agenda completa"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md shadow-purple-500/25">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Agenda</p>
                  <p className="text-xs text-gray-500">
                    {event.agenda?.days?.length
                      ? `${event.agenda.days.length} día(s) publicado(s)`
                      : isOrganizer
                        ? "Aún no has publicado la agenda"
                        : "Aún no se ha publicado la agenda"}
                  </p>
                </div>
              </div>
              <span className="text-purple-600 text-sm font-medium">
                Abrir →
              </span>
            </button>
          </div>
        </div>
      )}

      {activeTab === "ranking" && (
        <EventRankingTab
          event={event}
          registrations={registrations ?? []}
          isAdmin={isAdmin}
        />
      )}

      {activeTab === "activities" && (
        <EventActivitiesTab
          event={event}
          registrations={registrations ?? []}
          isAdmin={isAdmin}
          initialActivityId={hashActivityId ?? undefined}
        />
      )}

      {activeTab === "agenda" && (
        <EventAgendaPanel event={event} canEdit={isOrganizer} />
      )}

      {activeTab === "clubs" && (
        <EventClubsTab
          event={event}
          registrations={registrations}
          eventPayments={eventPayments}
        />
      )}

      {activeTab === "registrations" && (
        <div className="space-y-4">
          {/* Stats compactas + Controles */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 bg-white rounded-xl shadow-md">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1.5 text-sm">
                <Users className="w-4 h-4 text-purple-500" />
                <strong>{stats.total}</strong> total
              </span>
              <span className="flex items-center gap-1.5 text-sm">
                <Clock className="w-4 h-4 text-amber-500" />
                <strong className="text-amber-700">{stats.pending}</strong>{" "}
                pendientes
              </span>
              <span className="flex items-center gap-1.5 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <strong className="text-green-700">
                  {stats.approved}
                </strong>{" "}
                aprobados
              </span>
              {stats.rejected > 0 && (
                <span className="flex items-center gap-1.5 text-sm">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <strong>{stats.rejected}</strong> rechazados
                </span>
              )}
              <span className="flex items-center gap-1.5 text-sm ml-auto">
                <DollarSign className="w-4 h-4 text-emerald-500" />
                <strong>{formatCurrency(stats.totalAmount)}</strong>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar club..."
                  value={clubSearchTerm}
                  onChange={(e) => setClubSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 text-sm border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all w-48"
                />
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExportConsolidated}
                disabled={!registrations || registrations.length === 0}
                title="Exportar inscripciones de los clubes visibles a Excel"
              >
                <Download className="w-4 h-4 mr-1" />
                Exportar
              </Button>
            </div>
          </div>

          {/* Banner de aprobación masiva */}
          {isOrganizer && stats.pending > 0 && (
            <div className="flex items-center justify-between px-5 py-3 bg-amber-50 border-2 border-amber-200 rounded-xl">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">
                  {stats.pending} inscripción(es) pendiente(s) por aprobar
                </span>
              </div>
              <Can permission="events.approve">
                <Button
                  size="sm"
                  onClick={handleApproveAllPending}
                  disabled={isBulkProcessing}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isBulkProcessing ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-1" />
                  )}
                  Aprobar todos ({stats.pending})
                </Button>
              </Can>
            </div>
          )}

          {/* Resumen de clubes */}
          <div className="text-sm text-gray-500 flex items-center justify-between">
            <span>
              {filteredClubs.length} club
              {filteredClubs.length !== 1 ? "es" : ""}
              {clubSearchTerm && ` (filtrado de ${stats.byClub.length})`}
            </span>
            <button
              onClick={() => {
                if (expandedClubs.size === filteredClubs.length) {
                  setExpandedClubs(new Set());
                } else {
                  setExpandedClubs(new Set(filteredClubs.map((c) => c.clubId)));
                }
              }}
              className="text-purple-600 hover:text-purple-800 text-sm"
            >
              {expandedClubs.size === filteredClubs.length
                ? "Colapsar todos"
                : "Expandir todos"}
            </button>
          </div>

          {/* Lista de clubes */}
          {filteredClubs.length === 0 ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900">
                  {clubSearchTerm
                    ? "No se encontraron clubes"
                    : "No hay inscripciones"}
                </h3>
                <p className="text-gray-500 mt-1 text-sm">
                  {clubSearchTerm
                    ? `No hay clubes que coincidan con "${clubSearchTerm}"`
                    : "Aún no hay clubes inscritos en este evento"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredClubs.map((club) => {
                const clubRegs = registrationsByClub.get(club.clubId) || [];
                const filteredRegs = clubRegs.filter(
                  (r) => r.status !== "rejected" && r.status !== "cancelled",
                );
                const isExpanded = expandedClubs.has(club.clubId);

                return (
                  <Card
                    key={club.clubId}
                    className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-shadow"
                  >
                    {/* Header del club */}
                    <div
                      className={`px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer ${club.pending > 0 ? "bg-amber-50/40" : ""}`}
                    >
                      <button
                        onClick={() => toggleClubExpand(club.clubId)}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                        )}
                        <div className="w-9 h-9 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-sm shrink-0">
                          <Building2 className="w-4 h-4 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            {club.clubName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {club.approved} aprobado
                            {club.approved !== 1 ? "s" : ""}
                            {club.pending > 0 &&
                              ` • ${club.pending} pendiente${club.pending !== 1 ? "s" : ""}`}
                          </p>
                        </div>
                      </button>

                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {club.approved}
                        </Badge>
                        {club.pending > 0 && (
                          <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                            {club.pending}
                          </Badge>
                        )}
                        <span className="text-sm font-medium text-gray-700 w-24 text-right">
                          {formatCurrency(club.amount)}
                        </span>

                        {/* Inscribir miembros de este club (admin/organizador).
                            Para admins ignora el cierre por fecha. */}
                        {(isAdmin || isOrganizer) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setAdminRegisterClub({
                                id: club.clubId,
                                name: club.clubName,
                                // Sin la iglesia el modal no puede resolver el
                                // distrito y cobra tarifa plena a un club que
                                // tiene tarifa especial.
                                churchId: clubs?.find(
                                  (c) => c.id === club.clubId,
                                )?.churchId,
                              });
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors ml-2"
                            title="Inscribir miembros de este club"
                          >
                            <UserPlus className="w-3 h-3" />
                            Inscribir
                          </button>
                        )}

                        {/* Botón Aprobar club */}
                        {isOrganizer && club.pending > 0 && (
                          <Can permission="events.approve">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApproveClub(club.clubId);
                              }}
                              disabled={isBulkProcessing}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 ml-2"
                            >
                              {isBulkProcessing ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <CheckCircle className="w-3 h-3" />
                              )}
                              Aprobar {club.pending}
                            </button>
                          </Can>
                        )}
                      </div>
                    </div>

                    {/* Tabla expandible */}
                    {isExpanded && (
                      <div className="border-t bg-gray-50">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-gray-500 border-b bg-gray-100">
                                <th className="px-4 py-2 font-medium">
                                  Persona
                                </th>
                                <th className="px-4 py-2 font-medium">
                                  Categoría
                                </th>
                                <th className="px-4 py-2 font-medium">
                                  Estado
                                </th>
                                <th className="px-4 py-2 font-medium text-right">
                                  Monto
                                </th>
                                {isOrganizer && (
                                  <th className="px-4 py-2 font-medium text-center">
                                    Acciones
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredRegs.map((reg) => (
                                <tr
                                  key={reg.id}
                                  className={`border-b last:border-b-0 hover:bg-white transition-colors ${reg.status === "pending" ? "bg-amber-50/30" : ""}`}
                                >
                                  <td className="px-4 py-2">
                                    <Link
                                      to={`/members/${reg.personId}`}
                                      className="font-medium text-gray-900 hover:text-purple-700 hover:underline"
                                      title="Ver perfil del miembro"
                                    >
                                      {reg.personName}
                                    </Link>
                                    {reg.medicalNotes && (
                                      <p className="text-xs text-red-600">
                                        🏥 {reg.medicalNotes}
                                      </p>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-gray-600">
                                    {getCategoryLabel(reg.category)}
                                  </td>
                                  <td className="px-4 py-2">
                                    <Badge
                                      className={
                                        reg.status === "approved"
                                          ? "bg-green-100 text-green-700"
                                          : reg.status === "pending"
                                            ? "bg-yellow-100 text-yellow-700"
                                            : reg.status === "rejected"
                                              ? "bg-red-100 text-red-700"
                                              : "bg-gray-100 text-gray-700"
                                      }
                                    >
                                      {reg.status === "approved"
                                        ? "Aprobado"
                                        : reg.status === "pending"
                                          ? "Pendiente"
                                          : reg.status === "rejected"
                                            ? "Rechazado"
                                            : reg.status === "cancelled"
                                              ? "Cancelado"
                                              : reg.status}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-2 text-right font-medium">
                                    {formatCurrency(reg.unitPrice)}
                                  </td>
                                  {isOrganizer && (
                                    <td className="px-4 py-2">
                                      <div className="flex items-center justify-center gap-1 flex-wrap">
                                        {reg.status === "pending" ? (
                                          <>
                                            <button
                                              onClick={() =>
                                                handleApproveRegistration(
                                                  reg.id,
                                                )
                                              }
                                              disabled={
                                                updateRegistrationStatus.isPending ||
                                                isBulkProcessing
                                              }
                                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
                                              title="Aprobar"
                                            >
                                              <CheckCircle className="w-3 h-3" />
                                              Aprobar
                                            </button>
                                            <button
                                              onClick={() =>
                                                handleRejectRegistration(reg.id)
                                              }
                                              disabled={
                                                updateRegistrationStatus.isPending ||
                                                isBulkProcessing
                                              }
                                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg border border-red-200 transition-colors disabled:opacity-50"
                                              title="Rechazar"
                                            >
                                              <XCircle className="w-3 h-3" />
                                              Rechazar
                                            </button>
                                          </>
                                        ) : null}
                                        {isAdmin && (
                                          <button
                                            type="button"
                                            onClick={() => setAdminEditReg(reg)}
                                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50 rounded-lg border border-purple-200"
                                            title="Editar inscripción (admin)"
                                          >
                                            <Pencil className="w-3 h-3" />
                                            Editar
                                          </button>
                                        )}
                                        {reg.status !== "pending" &&
                                          !isAdmin && (
                                            <span className="text-gray-300 text-center block">
                                              —
                                            </span>
                                          )}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              ))}
                              {filteredRegs.length === 0 && (
                                <tr>
                                  <td
                                    colSpan={6}
                                    className="px-4 py-4 text-center text-gray-500"
                                  >
                                    No hay inscripciones para mostrar
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === "payments" && (
        <div className="space-y-6">
          {/* Progress bar de recaudación */}
          {stats.totalAmount > 0 && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    Avance de recaudación
                  </p>
                  <p className="text-sm font-bold text-gray-900">
                    {Math.round((stats.paidAmount / stats.totalAmount) * 100)}%
                  </p>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${stats.paidAmount >= stats.totalAmount ? "bg-gradient-to-r from-emerald-400 to-emerald-600" : stats.paidAmount > stats.totalAmount * 0.5 ? "bg-gradient-to-r from-blue-400 to-blue-600" : "bg-gradient-to-r from-amber-400 to-amber-600"}`}
                    style={{
                      width: `${Math.min((stats.paidAmount / stats.totalAmount) * 100, 100)}%`,
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formatCurrency(stats.paidAmount)} recaudado de{" "}
                  {formatCurrency(stats.totalAmount)} total · Pendiente:{" "}
                  {formatCurrency(stats.pendingAmount)}
                </p>
              </CardContent>
            </Card>
          )}
          {/* Resumen de Pagos por Club */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
              <CardContent
                className="p-4"
                title="Suma del precio unitario de cada inscripción APROBADA. No incluye inscripciones pendientes ni rechazadas."
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Total a Cobrar
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(stats.totalAmount)}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {stats.approved} inscripción
                      {stats.approved !== 1 ? "es" : ""} aprobada
                      {stats.approved !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
              <CardContent
                className="p-4"
                title="Pagos enviados por los clubes que aún no has revisado. Aparecen en 'Validar Pagos'."
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/25 group-hover:scale-110 transition-transform duration-300">
                    <Hourglass className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Por Verificar
                    </p>
                    <p className="text-xl font-bold text-yellow-600">
                      {formatCurrency(
                        eventPayments
                          ?.filter((p) => p.status === "pending")
                          .reduce((sum, p) => sum + p.amount, 0) || 0,
                      )}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {eventPayments?.filter((p) => p.status === "pending")
                        .length || 0}{" "}
                      pago
                      {(eventPayments?.filter((p) => p.status === "pending")
                        .length || 0) !== 1
                        ? "s"
                        : ""}{" "}
                      sin revisar
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
              <CardContent
                className="p-4"
                title="Suma de los pagos que ya APROBASTE como organizador."
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/25 group-hover:scale-110 transition-transform duration-300">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Recaudado
                    </p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(stats.paidAmount)}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {stats.byPaymentStatus.paid} inscripción
                      {stats.byPaymentStatus.paid !== 1 ? "es" : ""} pagada
                      {stats.byPaymentStatus.paid !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {(() => {
              const diff = stats.pendingAmount; // total - recaudado
              const excedente = diff < 0;
              const label = excedente ? "Excedente" : "Por Cobrar";
              const value = excedente ? -diff : diff;
              const color = excedente ? "text-emerald-600" : "text-purple-600";
              const helper = excedente
                ? "Hay más recaudado que aprobado. Revisa si faltan inscripciones por aprobar."
                : "Saldo que aún no entra: Total a Cobrar menos Recaudado.";
              const sub = excedente
                ? `+${formatCurrency(value)} sobre lo facturado`
                : "saldo sin cubrir";
              return (
                <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
                  <CardContent
                    className="p-4"
                    title={`Fórmula: Total a Cobrar (${formatCurrency(stats.totalAmount)}) − Recaudado (${formatCurrency(stats.paidAmount)}) = ${formatCurrency(diff)}\n\n${helper}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          {label}
                        </p>
                        <p className={`text-xl font-bold ${color}`}>
                          {formatCurrency(value)}
                        </p>
                        <p className="text-[10px] text-gray-400">{sub}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}
          </div>

          {/* Aviso explicativo cuando hay desbalance entre inscripciones y pagos */}
          {stats.pendingAmount < 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-semibold mb-1">
                ⚠️ Hay más pagos aprobados que inscripciones aprobadas
              </p>
              <p>
                Recaudaste <b>{formatCurrency(stats.paidAmount)}</b> pero solo
                tienes <b>{stats.approved} inscripción(es) aprobada(s)</b> (
                {formatCurrency(stats.totalAmount)} facturado). Es posible que
                falten inscripciones por aprobar en la pestaña
                <i> Inscripciones</i>, o que algunos pagos correspondan a clubes
                cuya inscripción se rechazó/canceló después.
              </p>
            </div>
          )}

          {/* Pagos por Club */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Pagos por Club</CardTitle>
              {isOrganizer && (
                <Button
                  size="sm"
                  onClick={() => setShowPaymentsPanel(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  Validar Pagos
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {!eventPayments || eventPayments.length === 0 ? (
                <p className="text-gray-500 text-center py-6">
                  No hay pagos registrados para este evento
                </p>
              ) : (
                <div className="space-y-3">
                  {(() => {
                    // Combine registration data (what's owed) with payment data (what's been paid)
                    const byClub = new Map<
                      string,
                      {
                        clubName: string;
                        payments: number;
                        approved: number;
                        approvedAmount: number;
                        owedAmount: number;
                      }
                    >();
                    // First: get owed amounts from registrations
                    stats.byClub.forEach((club) => {
                      byClub.set(club.clubId, {
                        clubName: club.clubName,
                        payments: 0,
                        approved: 0,
                        approvedAmount: 0,
                        owedAmount: club.amount,
                      });
                    });
                    // Then: overlay payment data
                    eventPayments.forEach((p) => {
                      if (!byClub.has(p.clubId)) {
                        byClub.set(p.clubId, {
                          clubName: p.clubName,
                          payments: 0,
                          approved: 0,
                          approvedAmount: 0,
                          owedAmount: 0,
                        });
                      }
                      const c = byClub.get(p.clubId)!;
                      c.payments++;
                      if (p.status === "approved") {
                        c.approved++;
                        c.approvedAmount += p.amount;
                      }
                    });
                    return Array.from(byClub.entries())
                      .sort((a, b) =>
                        a[1].clubName.localeCompare(b[1].clubName),
                      )
                      .map(([clubId, data]) => (
                        <div
                          key={clubId}
                          className="p-4 border-2 border-gray-100 rounded-xl hover:shadow-md transition-all duration-300"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-5 h-5 text-gray-400" />
                              <span className="font-medium">
                                {data.clubName}
                              </span>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">
                                {formatCurrency(data.approvedAmount)} /{" "}
                                {formatCurrency(data.owedAmount)}
                              </p>
                              <p className="text-sm text-gray-500">
                                {data.approved} pago
                                {data.approved !== 1 ? "s" : ""} aprobado
                                {data.approved !== 1 ? "s" : ""}
                                {data.owedAmount > 0 &&
                                  data.approvedAmount < data.owedAmount &&
                                  ` · Faltan ${formatCurrency(data.owedAmount - data.approvedAmount)}`}
                              </p>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                data.owedAmount > 0 &&
                                data.approvedAmount >= data.owedAmount
                                  ? "bg-green-500"
                                  : data.approvedAmount > 0
                                    ? "bg-yellow-500"
                                    : "bg-gray-300"
                              }`}
                              style={{
                                width: `${data.owedAmount > 0 ? Math.min((data.approvedAmount / data.owedAmount) * 100, 100) : 0}%`,
                              }}
                            />
                          </div>
                        </div>
                      ));
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ingresos Esperados por Categoría (basado en inscripciones aprobadas) */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Ingresos Esperados por Categoría</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.byCategory).length === 0 ? (
                <p className="text-gray-500 text-center py-4">Sin datos</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(stats.byCategory).map(([category, data]) => (
                    <div
                      key={category}
                      className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-sm shadow-purple-500/25">
                          <Users className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-medium">
                            {getCategoryLabel(category)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {data.count} inscrito{data.count !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                      <p className="font-bold text-lg">
                        {formatCurrency(data.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === "settings" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuración General */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Configuración General</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                  <p className="text-sm text-gray-500">Nivel del Evento</p>
                  <p className="font-medium capitalize">{event.level}</p>
                </div>
                <div className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                  <p className="text-sm text-gray-500">Tipo de Evento</p>
                  <p className="font-medium">
                    {eventTypeLabels[event.eventType]}
                  </p>
                </div>
                <div className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                  <p className="text-sm text-gray-500">Cupo Máximo</p>
                  <p className="font-medium">
                    {event.registrationConfig.maxCapacity || "Sin límite"}
                  </p>
                </div>
                <div className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                  <p className="text-sm text-gray-500">Requiere Aprobación</p>
                  <p className="font-medium">
                    {event.registrationConfig.requiresApproval ? "Sí" : "No"}
                  </p>
                </div>
                <div className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                  <p className="text-sm text-gray-500">Lista de Espera</p>
                  <p className="font-medium">
                    {event.registrationConfig.allowWaitlist
                      ? "Habilitada"
                      : "Deshabilitada"}
                  </p>
                </div>
                <div className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                  <p className="text-sm text-gray-500">Estado Actual</p>
                  <Badge className={getEventStatusColor(event.status)}>
                    {getEventStatusLabel(event.status)}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tipos de Club */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Clubes Permitidos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(
                  [
                    "aventureros",
                    "conquistadores",
                    "guias_mayores",
                  ] as ClubType[]
                ).map((type) => (
                  <div
                    key={type}
                    className={`p-3 rounded-xl border-2 flex items-center justify-between transition-all duration-300 ${
                      event.clubTypesAllowed.includes(type)
                        ? "bg-gradient-to-r from-green-50 to-green-100/50 border-green-200"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <span className="font-medium">{clubTypeLabels[type]}</span>
                    {event.clubTypesAllowed.includes(type) ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Precios Configurados */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Estructura de Precios</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                <table className="w-full min-w-[420px]">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b">
                      <th className="pb-2">Categoría</th>
                      <th className="pb-2 text-right">Precio Normal</th>
                      <th className="pb-2 text-right">Pronto Pago</th>
                    </tr>
                  </thead>
                  <tbody>
                    {event.pricing.map((price) => (
                      <tr
                        key={price.category}
                        className="border-b last:border-b-0"
                      >
                        <td className="py-3 font-medium">{price.label}</td>
                        <td className="py-3 text-right">
                          {formatCurrency(price.amount)}
                        </td>
                        <td className="py-3 text-right text-green-600">
                          {price.earlyBirdAmount
                            ? formatCurrency(price.earlyBirdAmount)
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Fechas Importantes */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Fechas Importantes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-purple-600" />
                    <span>Inicio del Evento</span>
                  </div>
                  <span className="font-medium">
                    {formatShortDate(event.startDate)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-purple-600" />
                    <span>Fin del Evento</span>
                  </div>
                  <span className="font-medium">
                    {formatShortDate(event.endDate)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span>Apertura de Inscripciones</span>
                  </div>
                  <span className="font-medium">
                    {formatShortDate(event.registrationConfig.startDate)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span>Cierre de Inscripciones</span>
                  </div>
                  <span className="font-medium">
                    {formatShortDate(event.registrationConfig.endDate)}
                  </span>
                </div>
                {event.registrationConfig.paymentDeadline && (
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span>Límite de Pago</span>
                    </div>
                    <span className="font-medium">
                      {formatShortDate(
                        event.registrationConfig.paymentDeadline,
                      )}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modals */}
      <EventModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        organizerId={event.organizerId}
        organizerName={event.organizerName}
        event={event}
      />

      <PaymentValidationPanel
        isOpen={showPaymentsPanel}
        onClose={() => setShowPaymentsPanel(false)}
        event={event}
      />

      {docsModal && (
        <MemberConsentDocsModal
          isOpen
          onClose={() => setDocsModal(null)}
          consent={docsModal.consent}
          memberName={docsModal.memberName}
        />
      )}

      {/* Modal admin: edición avanzada de inscripción */}
      <AdminRegistrationEditModal
        isOpen={adminEditReg !== null}
        onClose={() => setAdminEditReg(null)}
        registration={adminEditReg}
        onSwap={(r) => setSwapReg(r)}
      />

      {/* Modal director: cambiar tipo de entrada (categoría) - movido al inicio del JSX */}

      {/* Modal: rechazar inscripción con motivo */}
      <Modal
        isOpen={rejectRegistrationId !== null}
        onClose={() => setRejectRegistrationId(null)}
        size="sm"
        title="Rechazar inscripción"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Esta acción marca la inscripción como rechazada. El director del
            club verá el estado y el motivo en su panel "Mis Inscritos".
          </p>
          <div>
            <label
              htmlFor="reject-reason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Motivo del rechazo (opcional)
            </label>
            <textarea
              id="reject-reason"
              value={rejectReasonDraft}
              onChange={(e) => setRejectReasonDraft(e.target.value)}
              rows={3}
              placeholder="Ej: cupo excedido, persona no cumple requisitos…"
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-red-500/20 focus:border-red-500 text-sm transition-all"
            />
          </div>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => setRejectRegistrationId(null)}
              disabled={updateRegistrationStatus.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={submitRejectRegistration}
              disabled={updateRegistrationStatus.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {updateRegistrationStatus.isPending ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-1" />
              )}
              Rechazar inscripción
            </Button>
          </ModalFooter>
        </div>
      </Modal>

      {/* Admin/organizador: inscribir miembros de un club específico.
          El modal pasa skipDateChecks: isAdmin → los admins pueden inscribir
          aunque las inscripciones ya estén cerradas por fecha. */}
      {adminRegisterClub && (
        <EventRegistrationModal
          isOpen={!!adminRegisterClub}
          onClose={() => setAdminRegisterClub(null)}
          event={event}
          clubId={adminRegisterClub.id}
          clubName={adminRegisterClub.name}
          clubChurchId={adminRegisterClub.churchId}
          registeredPersonIds={
            registrations
              ?.filter(
                (r) =>
                  r.clubId === adminRegisterClub.id &&
                  !["rejected", "cancelled"].includes(r.status),
              )
              .map((r) => r.personId) ?? []
          }
        />
      )}
    </div>
  );
}
